/** Mock Sign Builder catalog — ported from rukh-pd-queue/origin/frontend/data/sign-builder-data.js */

export interface MockSubstrate {
  id: string
  label: string
  piq: string
  matType: string
  cats: string[]
  sizes?: { w: number; h: number; stock: string; label?: string }[]
  rollWidths?: number[]
  localOnly?: string
}

export interface MockCatalogItem {
  id: string
  label: string
  piq?: string
  localOnly?: string
}

export interface SOPWarning {
  level: 'error' | 'warning' | 'info'
  text: string
}

export interface MockSignCategory {
  id: string
  label: string
  group: string
}

export interface LabeledSelectOption {
  value: string
  label: string
}

export interface SopSectionEntry {
  id: string
  title: string
  summary: string
  fullText: string
  applies_to: string[]
}

export interface SignGuideDecisionOption {
  label: string
  ids?: string[]
  tip?: string
}

export interface SignGuideDecisionHelper {
  question: string
  options: SignGuideDecisionOption[]
}

export interface SignGuideGlossaryEntry {
  term: string
  def: string
}

export interface SignGuideCategoryExample {
  examples: string[]
  note?: string
}

export interface SignGuideSection {
  title: string
  overview?: string
  categoryExamples?: Record<string, SignGuideCategoryExample>
  decisionHelper?: SignGuideDecisionHelper
  glossary?: SignGuideGlossaryEntry[]
}

export interface SignGuideMaterial {
  name: string
  what: string
  durability?: string
  bestFor?: string
  avoid?: string
  sopRef?: string
}

export interface SignGuideContent {
  sections: Record<number, SignGuideSection>
  materials: Record<string, SignGuideMaterial>
}

export interface SizeExceedsRawResult {
  exceeds: boolean
  maxW?: number
  maxH?: number
  maxRollWidth?: number
  type?: 'rigid' | 'flexible'
  hasOversizeOption?: boolean
}

export interface SpecialOrderTierResult {
  tier: 'stock' | 'special-order' | 'over-all' | null
  soW?: number
  soH?: number
  /**
   * UI-only discriminator for SPECIAL ORDER banner copy (SB-STOCK-SIZE-FIX / SB-SO-ONLY-CANON).
   * Not written to compiled `special_order_tier` on the wire.
   * - so-only: zero Stock sheets; material is SO-only (Taylor immediate banner)
   * - exceeds-stock: Stock sheets exist, none fit, SO sheet fits
   */
  soReason?: 'so-only' | 'exceeds-stock'
}

/** Size-row stock classifiers (shared by SO-only predicate + tier check). */
function isStockSheet(s: { stock?: string }): boolean {
  return /stock/i.test(s.stock || '') && !/special/i.test(s.stock || '')
}
function isSoSheet(s: { stock?: string }): boolean {
  return /special|so/i.test(s.stock || '')
}

/**
 * SB-SO-ONLY-CANON: substrate has sizes and zero Stock rows (all SO).
 * Today: PVC_0.5IN_WHITE_OUTDOOR, PVC_1IN_WHITE_OUTDOOR.
 */
export function isSpecialOrderOnly(sub: MockSubstrate | undefined | null): boolean {
  if (!sub?.sizes?.length) return false
  return sub.sizes.every(isSoSheet) && !sub.sizes.some(isStockSheet)
}

/**
 * Dropdown label: Local Only first, then Special Order Only (Taylor: "after Local Only: LAN").
 * Example: `PVC 0.5" White (Outdoor)  (PVC_0.5IN_WHITE_OUTDOOR) — Local Only: LAN — Special Order Only`
 */
export function formatSubstrateOptionLabel(sub: MockSubstrate): string {
  let label = `${sub.label}  (${sub.piq})`
  if (sub.localOnly) {
    label += ` — Local Only: ${sub.localOnly}`
  }
  if (isSpecialOrderOnly(sub)) {
    label += ' — Special Order Only'
  }
  return label
}

/** F-060 — the paper sticker substrate and the affordable-size rule (inches, either orientation). */
export const PAPER_STICKER_SUBSTRATE = 'PAPER_60LB_DECAL'
export const PAPER_STICKER_AFFORDABLE_MAX: readonly [number, number] = [12.75, 18.75]
export const PAPER_STICKER_NOTE_TEXT =
  'Small format: the affordable paper sticker (60# decal paper) is 12.75 × 18.75" or smaller.'
export const PAPER_STICKER_OVERSIZE_TEXT =
  'Small format: this size is larger than the affordable paper sticker (60# decal paper, 12.75 × 18.75" max) — confirm the build and price with the customer.'

/** Taylor so-only banner body (SB-SO-ONLY-CANON; supersedes interim sheet-dimension copy). */
export const SPECIAL_ORDER_ONLY_BANNER_BODY =
  'Special-Order material — extra cost and lead time. Confirm with the customer.'

/**
 * True stock-miss banner body (ACM etc.): finished size exceeds Stock, routes to SO sheet.
 * Unchanged from pre-F1 copy.
 */
export function specialOrderExceedsStockMessage(
  reqW: number,
  reqH: number,
  soW: number,
  soH: number
): string {
  return `Finished ${reqW}" × ${reqH}" exceeds the standard Stock sheet. Routes to a ${soW}×${soH}" Special-Order sheet — extra cost and lead time. Confirm with the customer.`
}

/** SOP 6.2.1.1 materials that may stay at full sheet if no finishing (cite getSOPWarnings). */
export const FULL_SHEET_APPROVED_SUBSTRATES = [
  'ACM_3MM_WHITE',
  'ACM_6MM_WHITE',
  'ACRYLIC_0.125IN_CLEAR',
  'ACRYLIC_0.125IN_WHITE',
  'ACRYLIC_0.125IN_BLACK_P95',
  'ACRYLIC_0.25IN_CLEAR',
  'ACRYLIC_0.25IN_WHITE',
  'ACRYLIC_0.25IN_BLACK',
  'ALUMINUM_.080IN_WHITE',
  'ALUMINUM_.080IN_MILL',
  'ALUMINUM_.040IN_WHITE',
  'BUBBLE-X_4MM_WHITE',
  'BUBBLE-X_5MM_WHITE',
  'COROPLAST_10MM_WHITE',
  'POLYSTYRENE_.030IN_WHITE',
  'POLYSTYRENE_.060IN_WHITE',
  'PVC_3MM_WHITE_INDOOR',
  'PVC_12MM_WHITE_INDOOR',
  'PVC_12MM_BLACK_INDOOR',
  'PVC_1IN_WHITE_INDOOR',
  'PVC_6MM_WHTE_INDOOR',
  'SCM_3MM_WHITE',
] as const

/**
 * SB-STOCK-SIZE-FIX F3 (canon ADDITION beyond reference): while SOP 6.2.1 full-sheet
 * warning would fire, Rounded Corners + Mounting Holes must be disabled and cleared.
 * Owner outranks reference (ref only shows the note).
 */
export function isFullSheetFinishingLocked(
  substrateId: string | undefined | null,
  width: number,
  height: number
): boolean {
  if (!substrateId || !width || !height) return false
  const is48x96 = (width >= 48 && height >= 96) || (width >= 96 && height >= 48)
  if (!is48x96) return false
  return (FULL_SHEET_APPROVED_SUBSTRATES as readonly string[]).includes(substrateId)
}

export const SIGN_CATEGORIES: MockSignCategory[] = [
  { id: 'post-mounted-longterm', label: 'Outdoor Permanent Signs (posts, stakes)', group: 'Outdoor Signs' },
  { id: 'post-mounted-temp', label: 'Outdoor Temporary Signs (yard signs, events)', group: 'Outdoor Signs' },
  { id: 'wall-fence-exterior', label: 'Outdoor Wall & Fence Signs', group: 'Outdoor Signs' },
  { id: 'pylons-lightboxes', label: 'Pylons & Lightboxes', group: 'Outdoor Signs' },
  { id: 'wall-boards-interior', label: 'Indoor Wall Signs & Boards', group: 'Indoor Signs' },
  { id: 'a-boards-yard', label: 'A-Frames & Sidewalk Signs', group: 'Indoor Signs' },
  { id: 'dimensional', label: '3D Letters & Logos', group: 'Indoor Signs' },
  { id: 'banners', label: 'Banners (indoor & outdoor)', group: 'Banners & Displays' },
  { id: 'vinyl-wall', label: 'Wall Graphics & Murals', group: 'Graphics & Vinyl' },
  { id: 'vinyl-window', label: 'Window Graphics & Film', group: 'Graphics & Vinyl' },
  { id: 'vinyl-floor', label: 'Floor Graphics', group: 'Graphics & Vinyl' },
  { id: 'decals-stickers', label: 'Decals & Stickers', group: 'Graphics & Vinyl' },
  { id: 'magnets', label: 'Vehicle & Fridge Magnets', group: 'Other' },
  { id: 'folded-displays', label: 'Tabletop & Counter Displays', group: 'Other' },
  { id: 'ada-specialty', label: 'Braille & ADA Signs', group: 'Indoor Signs' },
  { id: 'small-format', label: 'Small Format Printed Pieces', group: 'Other' },
  { id: 'posters-prints', label: 'Posters & Prints', group: 'Other' },
  { id: 'other', label: 'Something Else / Not Sure', group: 'Other' },
]

export const SUBSTRATES: MockSubstrate[] = [
  {
    "id": "ALUMINUM_.080IN_WHITE",
    "label": "Aluminum .080\" White",
    "piq": "ALUMINUM_.080IN_WHITE",
    "matType": "rigid",
    "cats": [
      "post-mounted-longterm",
      "wall-fence-exterior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "EVT"
  },
  {
    "id": "ALUMINUM_.080IN_MILL",
    "label": "Aluminum .080\" Mill Finish",
    "piq": "ALUMINUM_.080IN_MILL",
    "matType": "rigid",
    "cats": [
      "post-mounted-longterm",
      "wall-fence-exterior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "ALUMINUM_.040IN_WHITE",
    "label": "Aluminum .040\" White",
    "piq": "ALUMINUM_.040IN_WHITE",
    "matType": "rigid",
    "cats": [
      "post-mounted-longterm",
      "wall-fence-exterior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACM_3MM_WHITE",
    "label": "ACM 3mm White",
    "piq": "ACM_3MM_WHITE",
    "matType": "rigid",
    "cats": [
      "post-mounted-temp",
      "wall-fence-exterior",
      "wall-boards-interior",
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      },
      {
        "w": 60,
        "h": 120,
        "stock": "Special Order"
      }
    ]
  },
  {
    "id": "ACM_6MM_WHITE",
    "label": "ACM 6mm White",
    "piq": "ACM_6MM_WHITE",
    "matType": "rigid",
    "cats": [
      "wall-fence-exterior",
      "wall-boards-interior",
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      },
      {
        "w": 60,
        "h": 120,
        "stock": "Special Order"
      }
    ]
  },
  {
    "id": "ACM_3MM_BLACK_GLOSS",
    "label": "ACM 3mm Black Gloss",
    "piq": "ACM_3MM_BLACK_GLOSS",
    "matType": "rigid",
    "cats": [
      "wall-fence-exterior",
      "wall-boards-interior",
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "ACM_3MM_BLACK_MATTE",
    "label": "ACM 3mm Black Matte",
    "piq": "ACM_3MM_BLACK_MATTE",
    "matType": "rigid",
    "cats": [
      "wall-fence-exterior",
      "wall-boards-interior",
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "ACM_3MM_BRUSHED_SILVER",
    "label": "ACM 3mm Brushed Silver",
    "piq": "ACM_3MM_BRUSHED_SILVER",
    "matType": "rigid",
    "cats": [
      "wall-fence-exterior",
      "wall-boards-interior",
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      },
      {
        "w": 60,
        "h": 120,
        "stock": "Stock"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "PVC_3MM_WHITE_INDOOR",
    "label": "PVC 3mm White (Indoor)",
    "piq": "PVC_3MM_WHITE_INDOOR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "PVC_3MM_BLACK_INDOOR",
    "label": "PVC 3mm Black (Indoor)",
    "piq": "PVC_3MM_BLACK_INDOOR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "PVC_6MM_WHTE_INDOOR",
    "label": "PVC 6mm White (Indoor)",
    "piq": "PVC_6MM_WHTE_INDOOR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "PVC_12MM_WHITE_INDOOR",
    "label": "PVC 12mm White (Indoor)",
    "piq": "PVC_12MM_WHITE_INDOOR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "PVC_12MM_BLACK_INDOOR",
    "label": "PVC 12mm Black (Indoor)",
    "piq": "PVC_12MM_BLACK_INDOOR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "PVC_1IN_WHITE_INDOOR",
    "label": "PVC 1\" White (Indoor)",
    "piq": "PVC_1IN_WHITE_INDOOR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "PVC_0.5IN_WHITE_OUTDOOR",
    "label": "PVC 0.5\" White (Outdoor)",
    "piq": "PVC_0.5IN_WHITE_OUTDOOR",
    "matType": "rigid",
    "cats": [
      "wall-fence-exterior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Special Order"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "PVC_1IN_WHITE_OUTDOOR",
    "label": "PVC 1\" White (Outdoor)",
    "piq": "PVC_1IN_WHITE_OUTDOOR",
    "matType": "rigid",
    "cats": [
      "wall-fence-exterior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Special Order"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "ACRYLIC_0.125IN_CLEAR",
    "label": "Acrylic 1/8\" Clear",
    "piq": "ACRYLIC_0.125IN_CLEAR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional",
      "ada-specialty",
      "pylons-lightboxes"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_0.125IN_WHITE",
    "label": "Acrylic 1/8\" White",
    "piq": "ACRYLIC_0.125IN_WHITE",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional",
      "ada-specialty",
      "pylons-lightboxes"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_0.125IN_BLACK_P95",
    "label": "Acrylic 1/8\" Black P95",
    "piq": "ACRYLIC_0.125IN_BLACK_P95",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional",
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_0.125IN_CLEAR_MIRROR",
    "label": "Acrylic 1/8\" Clear Mirror",
    "piq": "ACRYLIC_0.125IN_CLEAR_MIRROR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_0.25IN_CLEAR",
    "label": "Acrylic 1/4\" Clear",
    "piq": "ACRYLIC_0.25IN_CLEAR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional",
      "pylons-lightboxes"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_0.25IN_WHITE",
    "label": "Acrylic 1/4\" White",
    "piq": "ACRYLIC_0.25IN_WHITE",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional",
      "pylons-lightboxes"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_0.5IN_BLACK",
    "label": "Acrylic ½\" Black",
    "piq": "ACRYLIC_0.5IN_BLACK",
    "matType": "rigid",
    "cats": [
      "interior-wall",
      "dimensional"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_0.25IN_BLACK",
    "label": "Acrylic 1/4\" Black",
    "piq": "ACRYLIC_0.25IN_BLACK",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_0.25IN_CLEAR_NON_GLARE",
    "label": "Acrylic 1/4\" Clear Non-Glare",
    "piq": "ACRYLIC_0.25IN_CLEAR_NON_GLARE",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_.060IN_CLEAR",
    "label": "Acrylic .060\" Clear",
    "piq": "ACRYLIC_.060IN_CLEAR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "ACRYLIC_0.5IN_CLEAR",
    "label": "Acrylic ½\" Clear",
    "piq": "ACRYLIC_0.5IN_CLEAR",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior",
      "dimensional"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "ACRYLIC_1-8IN_COLOR_ROWMARK",
    "label": "Rowmark Color Acrylic ⅛\"",
    "piq": "ACRYLIC_1/8IN_COLOR_ROWMARK",
    "matType": "ada",
    "cats": [
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 24,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_1-16IN_COLOR_ROWMARK",
    "label": "Rowmark Color Acrylic 1/16\"",
    "piq": "ACRYLIC_1/16IN_COLOR_ROWMARK",
    "matType": "ada",
    "cats": [
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 24,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_1-32IN_COLOR_ROWMARK",
    "label": "Rowmark Color Acrylic 1/32\"",
    "piq": "ACRYLIC_1/32IN_COLOR_ROWMARK",
    "matType": "ada",
    "cats": [
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 24,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_ENGRAVING_1-8IN",
    "label": "Rowmark Engraving Acrylic ⅛\"",
    "piq": "ACRYLIC_ENGRAVING_1/8IN",
    "matType": "ada",
    "cats": [
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 24,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_ENGRAVING_1-16IN",
    "label": "Rowmark Engraving Acrylic 1/16\"",
    "piq": "ACRYLIC_ENGRAVING_1/16IN",
    "matType": "ada",
    "cats": [
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 24,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "ACRYLIC_ENGRAVING_1-32IN",
    "label": "Rowmark Engraving Acrylic 1/32\"",
    "piq": "ACRYLIC_ENGRAVING_1/32IN",
    "matType": "ada",
    "cats": [
      "ada-specialty"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 24,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "COROPLAST_4MM_WHITE",
    "label": "Coroplast 4mm White",
    "piq": "COROPLAST_4MM_WHITE",
    "matType": "rigid",
    "cats": [
      "a-boards-yard",
      "post-mounted-temp",
      "folded-displays"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      },
      {
        "w": 60,
        "h": 120,
        "stock": "Special Order"
      }
    ]
  },
  {
    "id": "COROPLAST_4MM_BLACK",
    "label": "Coroplast 4mm Black",
    "piq": "COROPLAST_4MM_BLACK",
    "matType": "rigid",
    "cats": [
      "a-boards-yard",
      "post-mounted-temp"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "COROPLAST_6MM_WHITE",
    "label": "Coroplast 6mm White",
    "piq": "COROPLAST_6MM_WHITE",
    "matType": "rigid",
    "cats": [
      "a-boards-yard",
      "post-mounted-temp"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "COROPLAST_10MM_WHITE",
    "label": "Coroplast 10mm White",
    "piq": "COROPLAST_10MM_WHITE",
    "matType": "rigid",
    "cats": [
      "folded-displays"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "POLYSTYRENE_.030IN_WHITE",
    "label": "Polystyrene .030\" White",
    "piq": "POLYSTYRENE_.030IN_WHITE",
    "matType": "rigid",
    "cats": [
      "a-boards-yard"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "POLYSTYRENE_.040IN_WHITE",
    "label": "Polystyrene .040\" White",
    "piq": "POLYSTYRENE_.040IN_WHITE",
    "matType": "rigid",
    "cats": [
      "a-boards-yard"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "POLYSTYRENE_.060IN_WHITE",
    "label": "Polystyrene .060\" White",
    "piq": "POLYSTYRENE_.060IN_WHITE",
    "matType": "rigid",
    "cats": [
      "folded-displays"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "POLYSTYRENE_.060IN_BLACK",
    "label": "Polystyrene .060\" Black",
    "piq": "POLYSTYRENE_.060IN_BLACK",
    "matType": "rigid",
    "cats": [
      "folded-displays"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "EVT & LAN"
  },
  {
    "id": "SCM_3MM_WHITE",
    "label": "SCM 3mm White",
    "piq": "SCM_3MM_WHITE",
    "matType": "rigid",
    "cats": [
      "wall-boards-interior"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "HONEYCOMB_BOARD_0.5IN_W-K-W",
    "label": "Honeycomb Board 0.5\"",
    "piq": "HONEYCOMB_BOARD_0.5IN_W-K-W",
    "matType": "rigid",
    "cats": [
      "folded-displays"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "FOAMCORE_0.1875IN_WHITE_ECONOM",
    "label": "Foamcore 3/16\" White",
    "piq": "FOAMCORE_0.1875IN_WHITE_ECONOM",
    "matType": "rigid",
    "cats": [
      "folded-displays"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ],
    "localOnly": "EVT & LAN"
  },
  {
    "id": "CONVERD_3MM_WHITE",
    "label": "Converd 3mm White",
    "piq": "CONVERD_3MM_WHITE",
    "matType": "rigid",
    "cats": [
      "folded-displays"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "BUBBLE-X_4MM_WHITE",
    "label": "Bubble-X 4mm White",
    "piq": "BUBBLE-X_4MM_WHITE",
    "matType": "rigid",
    "cats": [
      "post-mounted-temp"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "BUBBLE-X_5MM_WHITE",
    "label": "Bubble-X 5mm White",
    "piq": "BUBBLE-X_5MM_WHITE",
    "matType": "rigid",
    "cats": [
      "post-mounted-temp"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      }
    ]
  },
  {
    "id": "CARDSTOCK_.050IN_WHITE",
    "label": "Cardstock .050\" White",
    "piq": "CARDSTOCK_.050IN_WHITE",
    "matType": "rigid",
    "cats": [
      "posters-prints"
    ],
    "sizes": [
      {
        "w": 28,
        "h": 44,
        "stock": "Stock"
      }
    ],
    "localOnly": "EVT"
  },
  {
    "id": "POLYCARBONATE_0.185IN_WHITE_RL",
    "label": "Polycarbonate .185\" White",
    "piq": "POLYCARBONATE_0.185IN_WHITE_RL",
    "matType": "rigid",
    "cats": [
      "pylons-lightboxes"
    ],
    "sizes": [
      {
        "w": 48,
        "h": 96,
        "stock": "Stock"
      },
      {
        "w": 76,
        "h": 300,
        "stock": "Special Order"
      }
    ],
    "localOnly": "LAN"
  },
  {
    "id": "BANNER_13OZ_SCRIM_MATTE",
    "label": "Banner 13oz Scrim Matte",
    "piq": "BANNER_13OZ_SCRIM_MATTE",
    "matType": "flexible",
    "cats": [
      "banners"
    ],
    "rollWidths": [
      54,
      98
    ]
  },
  {
    "id": "FILM_ANTI_CURL_.010IN",
    "label": "Film Anti-Curl .010\"",
    "piq": "FILM_ANTI_CURL_.010IN",
    "matType": "flexible",
    "cats": [
      "banners"
    ],
    "rollWidths": [
      36
    ]
  },
  {
    "id": "FILM_.008IN_BLOCKOUT_DOUBLESID",
    "label": "Film .008\" Blockout Double-Sided",
    "piq": "FILM_.008IN_BLOCKOUT_DOUBLESID",
    "matType": "flexible",
    "cats": [
      "banners"
    ],
    "rollWidths": [
      54
    ],
    "localOnly": "LAN"
  },
  {
    "id": "MAGNETIC_.015IN_WHITE",
    "label": "Magnetic .015\" White",
    "piq": "MAGNETIC_.015IN_WHITE",
    "matType": "flexible",
    "cats": [
      "magnets"
    ],
    "rollWidths": [
      48
    ]
  },
  {
    "id": "MAGNETIC_.030IN_WHITE",
    "label": "Magnetic .030\" White",
    "piq": "MAGNETIC_.030IN_WHITE",
    "matType": "flexible",
    "cats": [
      "magnets"
    ],
    "rollWidths": [
      48
    ]
  },
  {
    "id": "MAGNETIC_.030IN_ADHESIVE",
    "label": "Magnetic .030\" Adhesive Back",
    "piq": "MAGNETIC_.030IN_ADHESIVE",
    "matType": "flexible",
    "cats": [
      "magnets"
    ],
    "rollWidths": [
      48
    ],
    "localOnly": "LAN"
  },
  {
    "id": "VINYL_CAL_INTERMEDIATE",
    "label": "Vinyl Cal Intermediate",
    "piq": "VINYL_CAL_INTERMEDIATE",
    "matType": "flexible",
    "cats": [
      "vinyl-wall",
      "vinyl-window",
      "decals-stickers"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "VINYL_CAST",
    "label": "Vinyl Cast",
    "piq": "VINYL_CAST",
    "matType": "flexible",
    "cats": [
      "vinyl-wall",
      "decals-stickers"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "VINYL_CAST_VEHICLE",
    "label": "Vinyl Cast Vehicle Wrap",
    "piq": "VINYL_CAST_VEHICLE",
    "matType": "flexible",
    "cats": [
      "vinyl-wall"
    ],
    "rollWidths": [
      54
    ],
    "localOnly": "EVT & LAN"
  },
  {
    "id": "VINYL_CAL_FLOOR",
    "label": "Vinyl Cal Floor",
    "piq": "VINYL_CAL_FLOOR",
    "matType": "flexible",
    "cats": [
      "vinyl-floor"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "VINYL_OPTICALLY_CLEAR_CAL",
    "label": "Optically Clear Vinyl",
    "piq": "VINYL_OPTICALLY_CLEAR_CAL",
    "matType": "flexible",
    "cats": [
      "vinyl-window"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "VINYL_PERFORATED_STOREFRONT",
    "label": "Perforated Storefront (70/30)",
    "piq": "VINYL_PERFORATED_STOREFRONT",
    "matType": "flexible",
    "cats": [
      "vinyl-window"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "VINYL_PERFORATED_VEHICLE",
    "label": "Perforated Vehicle (50/50)",
    "piq": "VINYL_PERFORATED_VEHICLE",
    "matType": "flexible",
    "cats": [
      "vinyl-window"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "VINYL_WALL_TEXTURED_SURFACE",
    "label": "Wall Textured Surface Vinyl",
    "piq": "VINYL_WALL_TEXTURED_SURFACE",
    "matType": "flexible",
    "cats": [
      "vinyl-wall"
    ],
    "rollWidths": [
      54
    ],
    "localOnly": "EVT & LAN"
  },
  {
    "id": "VINYL_TEXTILE_REPOSITIONABLE",
    "label": "Vinyl Textile Repositionable",
    "piq": "VINYL_TEXTILE_REPOSITIONABLE",
    "matType": "flexible",
    "cats": [
      "vinyl-wall"
    ],
    "rollWidths": [
      54
    ],
    "localOnly": "EVT"
  },
  {
    "id": "VINYL_RTA_FROSTED",
    "label": "RTA Frosted Vinyl",
    "piq": "VINYL_RTA_FROSTED",
    "matType": "flexible",
    "cats": [
      "vinyl-window"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "FILM_EXTERIOR_WALKWAY_ALUMIGRAPHICS",
    "label": "Exterior Walkway Alumigraphics",
    "piq": "FILM_EXTERIOR_WALKWAY_ALUMIGRAPHICS",
    "matType": "flexible",
    "cats": [
      "vinyl-floor"
    ],
    "rollWidths": [
      53
    ]
  },
  {
    "id": "CLING_PREMIUM_WHITE",
    "label": "Cling Premium White",
    "piq": "CLING_PREMIUM_WHITE",
    "matType": "flexible",
    "cats": [
      "vinyl-window"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "CLING_STATIC_CLEAR",
    "label": "Cling Static Clear",
    "piq": "CLING_STATIC_CLEAR",
    "matType": "flexible",
    "cats": [
      "vinyl-window"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "CLING_STATIC_WHITE",
    "label": "Cling Static White",
    "piq": "CLING_STATIC_WHITE",
    "matType": "flexible",
    "cats": [
      "vinyl-window"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "PAPER_POSTER_.011IN_SATIN",
    "label": "Paper Poster .011\" Satin",
    "piq": "PAPER_POSTER_.011IN_SATIN",
    "matType": "flexible",
    "cats": [
      "posters-prints"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "PAPER_PHOTO_.011IN_SATIN",
    "label": "Paper Photo .011\" Satin",
    "piq": "PAPER_PHOTO_.011IN_SATIN",
    "matType": "flexible",
    "cats": [
      "posters-prints"
    ],
    "rollWidths": [
      54
    ]
  },
  {
    "id": "CANVAS",
    "label": "Canvas",
    "piq": "CANVAS",
    "matType": "flexible",
    "cats": [
      "posters-prints"
    ],
    "rollWidths": [
      54
    ],
    "localOnly": "LAN"
  },
  {
    "id": "PAPER_100LB_TEXT_GLOSSY",
    "label": "100# Glossy Text Paper",
    "piq": "PAPER_100LB_TEXT_GLOSSY",
    "matType": "flexible",
    "cats": [
      "small-format"
    ],
    "rollWidths": [
      12
    ],
    "localOnly": "EVT"
  },
  {
    "id": "PAPER_130LB_COVER_DULL",
    "label": "130# Dull Cover Stock",
    "piq": "PAPER_130LB_COVER_DULL",
    "matType": "flexible",
    "cats": [
      "small-format"
    ],
    "rollWidths": [
      13
    ],
    "localOnly": "EVT"
  },
  {
    "id": "PAPER_130LB_COVER_UNCOATED",
    "label": "130# Uncoated Cover Stock",
    "piq": "PAPER_130LB_COVER_UNCOATED",
    "matType": "flexible",
    "cats": [
      "small-format"
    ],
    "rollWidths": [
      13
    ],
    "localOnly": "EVT"
  },
  {
    "id": "PAPER_14MIL_SYNTHETIC_SEMI-GLOSS",
    "label": "14mil Synthetic Semi-Gloss",
    "piq": "PAPER_14MIL_SYNTHETIC_SEMI-GLOSSY",
    "matType": "flexible",
    "cats": [
      "small-format"
    ],
    "rollWidths": [
      12
    ],
    "localOnly": "EVT"
  },
  {
    "id": "PAPER_60LB_DECAL",
    "label": "60# Decal Paper (Adhesive)",
    "piq": "PAPER_60LB_DECAL",
    "matType": "flexible",
    "cats": [
      "small-format"
    ],
    "rollWidths": [
      13
    ],
    "localOnly": "EVT"
  },
  {
    "id": "PAPER_SILVER_90#_POLY_ADHESIVE_12x18",
    "label": "Silver 90# Poly Adhesive 12×18\"",
    "piq": "PAPER_SILVER_90#_POLY_ADHESIVE_12x18",
    "matType": "flexible",
    "cats": [
      "small-format"
    ],
    "sizes": [
      {
        "w": 12,
        "h": 18,
        "stock": "Stock",
        "label": "12×18\" Stock"
      }
    ],
    "localOnly": "EVT"
  },
  {
    "id": "CUSTOMER_SUPPLIED",
    "label": "Customer Supplied Material",
    "piq": "CUSTOMER_SUPPLIED",
    "matType": "none",
    "cats": [],
    "sizes": [],
    "rollWidths": []
  }
]

export const GRAPHIC_METHODS: MockCatalogItem[] = [
  {
    "id": "direct-1st",
    "label": "Direct Print — 1st Surface (Front)"
  },
  {
    "id": "direct-2nd",
    "label": "Direct Print — 2nd Surface (Back/Reverse)"
  },
  {
    "id": "vinyl-applied",
    "label": "Vinyl Applied"
  },
  {
    "id": "rta",
    "label": "RTA (Ready-to-Apply Vinyl)"
  },
  {
    "id": "cut-vinyl",
    "label": "Cut Vinyl Lettering (Plotted)"
  },
  {
    "id": "none",
    "label": "None / No Graphics"
  },
  {
    "id": "estimator-decide",
    "label": "Estimating & Design Teams to Decide"
  }
]

export const VINYL_TYPES: MockCatalogItem[] = [
  {
    "id": "VINYL_CAL_INTERMEDIATE",
    "label": "Cal Intermediate",
    "piq": "VINYL_CAL_INTERMEDIATE"
  },
  {
    "id": "VINYL_CAST",
    "label": "Cast Vinyl",
    "piq": "VINYL_CAST"
  },
  {
    "id": "VINYL_REF_WHITE_EG_NON-PRISMATIC",
    "label": "Reflective EG Non-Prismatic",
    "piq": "VINYL_REF_WHITE_EG_NON-PRISMATIC"
  },
  {
    "id": "VINYL_REF_WHITE_HIP",
    "label": "Reflective HIP (High Intensity)",
    "piq": "VINYL_REF_WHITE_HIP"
  },
  {
    "id": "VINYL_CAL_FLOOR",
    "label": "Cal Floor",
    "piq": "VINYL_CAL_FLOOR"
  },
  {
    "id": "VINYL_OPTICALLY_CLEAR_CAL",
    "label": "Optically Clear",
    "piq": "VINYL_OPTICALLY_CLEAR_CAL"
  },
  {
    "id": "VINYL_WALL_TEXTURED_SURFACE",
    "label": "Wall Textured Surface",
    "piq": "VINYL_WALL_TEXTURED_SURFACE"
  },
  {
    "id": "CAST_RTA",
    "label": "Cast RTA",
    "piq": "CAST_RTA"
  }
]

export const LAMINATES: MockCatalogItem[] = [
  {
    "id": "none",
    "label": "None",
    "piq": ""
  },
  {
    "id": "LAM_CAL-GLOSS",
    "label": "Cal Gloss",
    "piq": "LAM_CAL-GLOSS"
  },
  {
    "id": "LAM_CAL-MATTE",
    "label": "Cal Matte",
    "piq": "LAM_CAL-MATTE"
  },
  {
    "id": "LAM_CAST-GLOSS",
    "label": "Cast Gloss",
    "piq": "LAM_CAST-GLOSS"
  },
  {
    "id": "LAM_CAST-MATTE",
    "label": "Cast Matte",
    "piq": "LAM_CAST-MATTE"
  },
  {
    "id": "LAM_ANTI-GRAFFITI",
    "label": "Anti-Graffiti",
    "piq": "LAM_ANTI-GRAFFITI"
  },
  {
    "id": "LAM_DRY-ERASE",
    "label": "Dry-Erase Laminate",
    "piq": "LAM_DRY-ERASE"
  },
  {
    "id": "LAM_FLOOR",
    "label": "Floor Laminate (Slip-Resistant)",
    "piq": "LAM_FLOOR"
  },
  {
    "id": "LAMINATE_OPTICALLY_CLEAR",
    "label": "Optically Clear",
    "piq": "LAMINATE_OPTICALLY_CLEAR"
  },
  {
    "id": "LIQUID_DRY-ERASE_LAM",
    "label": "Liquid Dry-Erase Lam",
    "piq": "LIQUID_DRY-ERASE_LAM",
    "localOnly": "EVT & LAN"
  },
  {
    "id": "LIQUID_GLOSS_LAM",
    "label": "Liquid Gloss Lam (Local Only)",
    "piq": "LIQUID_GLOSS_LAM",
    "localOnly": "LAN"
  }
]

export const RTA_COLORS: string[] = [
  "Black",
  "White",
  "Prime Blue",
  "Reflective Black"
]

export const TAPING_OPTIONS = ['', 'APP MASK', 'APP TAPE', 'APP TAPE / MASK'] as const

export const GROMMET_OPTIONS = ['', 'Every 24"', 'Every 18"', '4 corners only'] as const

export const HEM_OPTIONS = ['', 'All sides', 'Top & bottom only'] as const

export type TapingOption = (typeof TAPING_OPTIONS)[number]
export type GrommetOption = (typeof GROMMET_OPTIONS)[number]
export type HemOption = (typeof HEM_OPTIONS)[number]

/**
 * F-019 (Diane) — VSG-PRE0038 "Corner Radius Standardization for ACM and Aluminum Signage":
 * the create-path radii are the standard's three — 1/2" for small signs (≈ 8.5×11),
 * 1-1/2" under 48" on the longest edge, 3" over 48". Exceptions (full-size wall-mount
 * boards keep square corners; small-format items follow their own specs; a radius written
 * on the work order wins) go through "Custom…". Values are inches; the spec stores `${value}"`.
 */
export const ROUNDED_CORNER_OPTIONS: LabeledSelectOption[] = [
  { value: 'none', label: 'No / No change' },
  { value: '0.5', label: '1/2" radius — small signs (about 8.5×11")' },
  { value: '1.5', label: '1-1/2" radius — under 48" on the longest edge' },
  { value: '3', label: '3" radius — over 48" on the longest edge' },
  { value: 'custom', label: 'Custom…' },
]

/** F-019 — one-line reminder of the standard shown under the create-path dropdown. */
export const ROUNDED_CORNER_STANDARD_HINT =
  'Standard VSG-PRE0038 (ACM & aluminum): 1/2" small signs, 1-1/2" under 4 ft, 3" over 4 ft on the longest edge. Full-size wall boards stay square; small-format items and a radius given on the work order: use Custom.'

/**
 * Modify-path list — the pre-standard options, frozen: `buildModifyChanges` writes these
 * `{ id, label }` refs to the wire and the dropdown-migration baseline pins them
 * (modifyDropdownParityGate). Do not merge the create-path list here.
 */
export const MODIFY_ROUNDED_CORNER_OPTIONS: LabeledSelectOption[] = [
  { value: 'none', label: 'No / No change' },
  { value: '0.25', label: '1/4" radius' },
  { value: '0.5', label: '1/2" radius' },
  { value: '1', label: '1" radius' },
  { value: 'custom', label: 'Custom…' },
]

/** Sign Builder / create-path Holes select (e3dbc85). Ids byte-match reference. */
/**
 * F-090 (Bridgette) — pyramid signs always take the same holes (6 × 3/8"): one location
 * option carries the count and, when picked, the Sign Builder sets the diameter to 3/8"
 * (still editable). Stored like any other location: `{ location: 'pyramid-6-holes',
 * diameter: '0.375' }`; the D&D attribute reads "pyramid 6 holes, ⌀ 0.375"".
 */
export const PYRAMID_HOLE_LOCATION = 'pyramid-6-holes'
export const PYRAMID_HOLE_DIAMETER = '0.375'

export const HOLE_LOCATION_OPTIONS: LabeledSelectOption[] = [
  { value: 'none', label: 'None' },
  { value: 'all-4-corners', label: 'All 4 corners' },
  { value: 'top-2-corners', label: 'Top 2 corners only' },
  { value: 'bottom-2-corners', label: 'Bottom 2 corners only' },
  { value: 'along-top', label: 'Along top edge (multiple)' },
  { value: 'u-channel', label: 'U-channel holes' },
  { value: PYRAMID_HOLE_LOCATION, label: 'Pyramid — 6 holes, 3/8"' },
  { value: 'match-template', label: 'Match template / drawing' },
  { value: 'other', label: 'Other (specify in notes)' },
]

/**
 * Modify checkbox-era location list — frozen labels for wire option refs
 * (`buildModifyChanges` / Modify review). Do not merge create-path copy here.
 */
export const MODIFY_HOLE_LOCATION_OPTIONS: LabeledSelectOption[] = [
  { value: 'all-4-corners', label: 'All 4 corners' },
  { value: 'top-2-corners', label: 'Top 2 corners' },
  { value: 'bottom-2-corners', label: 'Bottom 2 corners' },
  { value: 'along-top', label: 'Along top edge' },
  { value: 'match-template', label: 'Match template' },
  { value: 'other', label: 'Other (see notes)' },
]

/** A1: ids byte-equal to reference; label-only change vs prior "Prod Dev picks". */
export const HOLE_DIAMETER_OPTIONS: LabeledSelectOption[] = [
  { value: 'match-hardware', label: 'Match hardware' },
  { value: '0.25', label: '1/4"' },
  { value: '0.375', label: '3/8"' },
  { value: '0.5', label: '1/2"' },
  { value: 'custom', label: 'Custom…' },
]

/** Modify wire/UI diameter labels frozen (checkbox-era) — keeps match-hardware label byte-identical. */
export const MODIFY_HOLE_DIAMETER_OPTIONS: LabeledSelectOption[] = [
  { value: 'match-hardware', label: 'Prod Dev picks' },
  { value: '0.25', label: '1/4"' },
  { value: '0.375', label: '3/8"' },
  { value: '0.5', label: '1/2"' },
  { value: 'custom', label: 'Custom…' },
]

export type WhiteInkOptionKey = 'flood' | 'spot' | 'fiveWS' | 'threeWS'

export interface WhiteInkOption {
  key: WhiteInkOptionKey
  label: string
  hint: string
  tag: string
  desc: string
}

export const WHITE_INK_OPTIONS: WhiteInkOption[] = [
  {
    key: 'flood',
    label: 'Flood white',
    hint: 'Solid white layer across entire print',
    tag: 'FLOOD WHITE',
    desc: 'Print white over entire sign',
  },
  {
    key: 'spot',
    label: 'Spot white',
    hint: 'White only behind specific elements',
    tag: 'SPOT WHITE',
    desc: 'Print white only in specific areas',
  },
  {
    key: 'fiveWS',
    label: '5WS (5-white spec)',
    hint: 'Five-pass white for premium clear acrylic',
    tag: '5WS',
    desc: 'Double-sided effect using five layers of ink. Color-White-Black-White-Color. Specify which design should be seen on each side.',
  },
  {
    key: 'threeWS',
    label: '3WS (3-white spec)',
    hint: 'Three-pass white — most clear substrates',
    tag: '3WS',
    desc: 'Color-enhancing method for backlit panels. Color-White-Color.',
  },
]

export function findSubstrate(id: string): MockSubstrate | undefined {
  return SUBSTRATES.find((s) => s.id === id)
}

export function findGraphicMethod(id: string): MockCatalogItem | undefined {
  return GRAPHIC_METHODS.find((g) => g.id === id)
}

export function findVinylType(id: string): MockCatalogItem | undefined {
  return VINYL_TYPES.find((v) => v.id === id)
}

export function findLaminate(id: string): MockCatalogItem | undefined {
  return LAMINATES.find((l) => l.id === id)
}

/**
 * Max raw material dimensions for a substrate (prototype sign-builder-data.js L122).
 * SB-STOCK-SIZE-FIX F2: sheeted = any non-empty `sizes` (rigid OR ada) — reference gated
 * on matType==='rigid' only, which hid Rowmark RAW / exceed warnings (canon 48×24).
 */
export function getSubstrateMaxSize(sub: MockSubstrate | undefined | null): {
  maxW: number
  maxH: number
  type: 'rigid'
} | {
  maxRollWidth: number
  type: 'flexible'
} | null {
  if (!sub) return null
  if (sub.sizes && sub.sizes.length > 0) {
    const maxW = Math.max(...sub.sizes.map((s) => s.w))
    const maxH = Math.max(...sub.sizes.map((s) => s.h))
    return { maxW, maxH, type: 'rigid' }
  }
  if (sub.matType === 'flexible' && sub.rollWidths && sub.rollWidths.length > 0) {
    const maxRollWidth = Math.max(...sub.rollWidths)
    return { maxRollWidth, type: 'flexible' }
  }
  return null
}

/** Whether finished size exceeds raw material (prototype sign-builder-data.js L138). */
export function checkSizeExceedsRaw(
  sub: MockSubstrate | undefined | null,
  reqW: number,
  reqH: number
): SizeExceedsRawResult {
  const info = getSubstrateMaxSize(sub)
  if (!info || !reqW || !reqH) return { exceeds: false }
  if (info.type === 'rigid') {
    const fitsNormal = reqW <= info.maxW && reqH <= info.maxH
    const fitsRotated = reqW <= info.maxH && reqH <= info.maxW
    const hasOversizeOption = Boolean(sub?.sizes && sub.sizes.length > 1)
    if (!fitsNormal && !fitsRotated) {
      return {
        exceeds: true,
        maxW: info.maxW,
        maxH: info.maxH,
        type: 'rigid',
        hasOversizeOption,
      }
    }
  }
  if (info.type === 'flexible') {
    const minDim = Math.min(reqW, reqH)
    if (minDim > info.maxRollWidth) {
      return { exceeds: true, maxRollWidth: info.maxRollWidth, type: 'flexible' }
    }
  }
  return { exceeds: false }
}

/**
 * Special-order tier for oversized stock sheets (prototype sign-builder-data.js L875).
 * SB-STOCK-SIZE-FIX / SB-SO-ONLY-CANON: tier outcomes unchanged for wire (`special_order_tier`);
 * adds UI-only `soReason` so banners do not lie with "exceeds Stock" on SO-only materials.
 */
export function checkSpecialOrderTier(
  sub: MockSubstrate | undefined | null,
  reqW: number,
  reqH: number
): SpecialOrderTierResult {
  if (!sub || !sub.sizes || sub.sizes.length === 0 || !reqW || !reqH) return { tier: null }
  const fits = (s: { w: number; h: number }) =>
    (reqW <= s.w && reqH <= s.h) || (reqW <= s.h && reqH <= s.w)
  const stockSheets = sub.sizes.filter(isStockSheet)
  const soSheets = sub.sizes.filter(isSoSheet)
  if (stockSheets.some(fits)) return { tier: 'stock' }
  const fittingSO = soSheets.filter(fits)[0]
  if (fittingSO) {
    return {
      tier: 'special-order',
      soW: fittingSO.w,
      soH: fittingSO.h,
      soReason: stockSheets.length === 0 ? 'so-only' : 'exceeds-stock',
    }
  }
  return { tier: 'over-all' }
}

const CAST_SUBSTRATE_IDS = ['VINYL_CAST', 'VINYL_CAST_VEHICLE'];
const CAL_SUBSTRATE_IDS = ['VINYL_CAL_INTERMEDIATE', 'VINYL_CAL_FLOOR', 'VINYL_OPTICALLY_CLEAR_CAL', 'VINYL_WALL_TEXTURED_SURFACE'];
const CAST_LAM_IDS: string[] = ['LAM_CAST-GLOSS', 'LAM_CAST-MATTE', 'LAMINATE_OPTICALLY_CLEAR']
const CAL_LAM_IDS: string[] = ['LAM_CAL-GLOSS', 'LAM_CAL-MATTE']
export function isSubstrateCast(subId: string): boolean {
  return CAST_SUBSTRATE_IDS.includes(subId)
}
export function isSubstrateCal(subId: string): boolean {
  return CAL_SUBSTRATE_IDS.includes(subId)
}
export function isSubstrateCastOrCal(subId: string): boolean {
  return isSubstrateCast(subId) || isSubstrateCal(subId)
}
export { CAST_LAM_IDS, CAL_LAM_IDS }

// ─── FILTERING DATA STRUCTURES ─────────────────────────────

// Substrates where direct print is blocked for Global (cut-only)
const BLACK_SUBSTRATES = [
  'COROPLAST_4MM_BLACK','POLYSTYRENE_.060IN_BLACK',
  'PVC_3MM_BLACK_INDOOR','PVC_6MM_WHTE_INDOOR','PVC_12MM_BLACK_INDOOR',
  'ACM_3MM_BLACK_GLOSS','ACM_3MM_BLACK_MATTE'
];

// Aluminum substrates: vinyl-applied only (no direct print for Global)
const ALUMINUM_SUBSTRATE_IDS = [
  'ALUMINUM_.080IN_WHITE','ALUMINUM_.080IN_MILL','ALUMINUM_.040IN_WHITE'
];

// Clear substrates: 2nd surface print enabled
const CLEAR_SUBSTRATE_IDS = [
  'ACRYLIC_0.125IN_CLEAR','ACRYLIC_0.125IN_CLEAR_MIRROR',
  'ACRYLIC_0.25IN_CLEAR','ACRYLIC_0.25IN_CLEAR_NON_GLARE',
  'ACRYLIC_0.5IN_CLEAR','VINYL_OPTICALLY_CLEAR_CAL',
  'POLYCARBONATE_0.185IN_WHITE_RL'
];

// Banner/flexible-only substrates: direct-1st only, no vinyl-applied
const BANNER_SUBSTRATE_IDS = [
  'BANNER_13OZ_SCRIM_MATTE','FILM_ANTI_CURL_.010IN',
  'FILM_EXTERIOR_WALKWAY_ALUMIGRAPHICS'
];

// Flexible substrates that support double-sided; all other flexible = single-sided only
const DOUBLE_SIDED_FLEXIBLE_IDS: string[] = [
  'BANNER_13OZ_SCRIM_MATTE',          // standard hanging/pole-pocket banners
  'FILM_.008IN_BLOCKOUT_DOUBLESID',   // blockout double-sided film (built for it)
  'VINYL_OPTICALLY_CLEAR_CAL',        // 5WS sandwich print (Color-Blockout-Color) for double-sided window graphics
];

// Substrates that must use no laminate (self-sufficient)
const NO_LAM_SUBSTRATE_IDS = [
  'FILM_EXTERIOR_WALKWAY_ALUMIGRAPHICS', // Alumigraphics — naturally slip-resistant
  'CLING_STATIC_CLEAR','CLING_STATIC_WHITE', // static clings — no lam
];

// Substrates that may only use liquid laminates (no film pressure lam)
const LIQUID_LAM_ONLY_SUBSTRATE_IDS = [
  'ACM_3MM_WHITE','SCM_3MM_WHITE' // wall boards — SOP 6.16.1/6.16.2
];

// Liquid laminate IDs (for filtering when liquid-only applies)
const LIQUID_LAM_IDS = ['LIQUID_DRY-ERASE_LAM','LIQUID_GLOSS_LAM','LAM_DRY-ERASE'];

// Vinyl types that require NO laminate
const NO_LAM_VINYL_IDS: string[] = [
  'VINYL_RTA_FROSTED',                // SOP 6.23.4.5
  'CAST_RTA',                         // SOP 6.23.4.4 — RTA window letters: NO Lam
  'VINYL_RTA_CAST_BLACK_GLOSS',       // RTA cut vinyls: no overlaminate
  'VINYL_RTA_CAST_BLACK_MATTE',
  'VINYL_RTA_CAST_LTBLUE',
  'VINYL_RTA_CAST_WHITE',
  'VINYL_RTA_REFLECTIVE_BLACK_EG',
];

export { DOUBLE_SIDED_FLEXIBLE_IDS, NO_LAM_VINYL_IDS }

// Vinyl → allowed laminate IDs mapping
const VINYL_LAM_MAP: Record<string, string[]> = {
  'VINYL_CAST':                        ['LAM_CAST-GLOSS','LAM_CAST-MATTE'],
  'VINYL_CAL_INTERMEDIATE':            ['LAM_CAL-GLOSS','LAM_CAL-MATTE','LAM_DRY-ERASE','LAM_ANTI-GRAFFITI','LAM_FLOOR'],
  'VINYL_CAL_FLOOR':                   ['LAM_FLOOR'],
  'VINYL_OPTICALLY_CLEAR_CAL':         ['LAMINATE_OPTICALLY_CLEAR','none'],
  'VINYL_PERFORATED_STOREFRONT':       ['LAMINATE_OPTICALLY_CLEAR','none'],
  'VINYL_PERFORATED_VEHICLE':          ['LAMINATE_OPTICALLY_CLEAR','none'],
  'VINYL_WALL_TEXTURED_SURFACE':       ['LAM_CAST-MATTE','LAM_CAST-GLOSS'],
  'VINYL_REF_WHITE_EG_NON-PRISMATIC':  ['LAM_CAL-GLOSS','LAM_CAL-MATTE'],
  'VINYL_REF_WHITE_HIP':               ['LAM_CAST-GLOSS'],
  'VINYL_TEXTILE_REPOSITIONABLE':      ['none'],
  'VINYL_WALL_CAL':                    ['LAM_CAL-GLOSS','LAM_CAL-MATTE','LAM_DRY-ERASE'],
};
// Vinyl IDs in NO_LAM_VINYL_IDS → [] (no lam)

// Category → allowed graphic method IDs
const CATEGORY_GRAPHIC_METHODS: Record<string, string[]> = {
  'post-mounted-longterm':   ['vinyl-applied'],
  'wall-fence-exterior':     ['vinyl-applied'],                    // long-term exterior
  'pylons-lightboxes':       ['direct-1st','direct-2nd','vinyl-applied','rta','none','estimator-decide'],
  'post-mounted-temp':       ['direct-1st','vinyl-applied'],
  'wall-fence-temp':         ['direct-1st'],
  'a-frames-sidewalk':       ['direct-1st'],
  'banners':                 ['direct-1st'],
  'floor-graphics':          ['direct-1st'],
  'wall-graphics':           ['vinyl-applied'],
  'window-graphics':         ['direct-1st','vinyl-applied','rta','cut-vinyl'],
  'dimensional':             ['direct-1st','direct-2nd','vinyl-applied','cut-vinyl'],
  'interior-wall':           ['direct-1st','direct-2nd'],
  'decals-stickers':         ['direct-1st','vinyl-applied','cut-vinyl','rta'],
  'magnets':                 ['direct-1st','vinyl-applied'],
  'folded-displays':         ['direct-1st'],
  'tabletop-counter':        ['direct-1st','vinyl-applied'],
  'ada-specialty':           [],                                   // not a digital print workflow
  'small-format':            ['direct-1st'],
  'posters-prints':          ['direct-1st'],
};

// Category → allowed vinyl type IDs (when vinyl-applied)
const CATEGORY_VINYL_TYPES: Record<string, string[]> = {
  'post-mounted-longterm':  ['VINYL_CAL_INTERMEDIATE','VINYL_CAST','VINYL_REF_WHITE_EG_NON-PRISMATIC','VINYL_REF_WHITE_HIP'],
  'wall-fence-exterior':    ['VINYL_CAL_INTERMEDIATE','VINYL_CAST','VINYL_REF_WHITE_EG_NON-PRISMATIC'],
  'post-mounted-temp':      ['VINYL_CAL_INTERMEDIATE','VINYL_REF_WHITE_EG_NON-PRISMATIC'],
  'wall-graphics':          ['VINYL_CAST','VINYL_WALL_TEXTURED_SURFACE','VINYL_TEXTILE_REPOSITIONABLE'],
  'window-graphics':        ['VINYL_OPTICALLY_CLEAR_CAL','VINYL_PERFORATED_STOREFRONT','VINYL_PERFORATED_VEHICLE','VINYL_CAL_INTERMEDIATE','VINYL_RTA_FROSTED','CAST_RTA'],
  'floor-graphics':         ['VINYL_CAL_FLOOR'],
  'dimensional':            ['CAST_RTA'],
  'decals-stickers':        ['VINYL_CAST','VINYL_CAL_INTERMEDIATE','VINYL_REF_WHITE_EG_NON-PRISMATIC'],
  'magnets':                ['VINYL_CAL_INTERMEDIATE','VINYL_REF_WHITE_EG_NON-PRISMATIC'],
  'tabletop-counter':       ['VINYL_CAL_INTERMEDIATE'],
  'interior-wall':          ['VINYL_CAL_INTERMEDIATE','VINYL_WALL_TEXTURED_SURFACE','VINYL_TEXTILE_REPOSITIONABLE'],
};

/**
 * SB-VINYL-FIX-001 — Taylor catalog correction (supersedes reference data):
 * ACM 3mm White + Vinyl Applied → Cal Intermediate + Reflective EG NP, both wall
 * categories; Outdoor lost Cast Vinyl by his final-list wording.
 * Reflective EG = Reflective EG Non-Prismatic only (do not inherit a future Prismatic id).
 * Key: category|substrate|graphicMethod — consulted before CATEGORY_VINYL_TYPES.
 */
const VINYL_COMBO_OVERRIDES: Record<string, string[]> = {
  'wall-boards-interior|ACM_3MM_WHITE|vinyl-applied': [
    'VINYL_CAL_INTERMEDIATE',
    'VINYL_REF_WHITE_EG_NON-PRISMATIC',
  ],
  'wall-fence-exterior|ACM_3MM_WHITE|vinyl-applied': [
    'VINYL_CAL_INTERMEDIATE',
    'VINYL_REF_WHITE_EG_NON-PRISMATIC',
  ],
};

// ─── FILTERING HELPER FUNCTIONS ────────────────────────────

/**
 * Returns the allowed substrate IDs for the given category.
 * Respects SUBSTRATES[].cats[] — a substrate must list the category to appear.
 */
export function getAvailableSubstrates(category: string): MockSubstrate[] {
  // "Something Else / Not Sure" → no restriction; show everything
  if (!category || category === 'other') return SUBSTRATES;
  return SUBSTRATES.filter(s => s.cats && s.cats.includes(category));
}

/**
 * Returns the allowed graphic method IDs for the current state.
 * Combines category-level rules with substrate-level rules.
 */
export function getAvailableGraphicMethods(category: string, substrate: string): MockCatalogItem[] {
  // Start from category allowlist (or all methods if no category)
  const catAllowed = CATEGORY_GRAPHIC_METHODS[category] || GRAPHIC_METHODS.map(g => g.id);

  let allowed = [...catAllowed];

  if (substrate) {
    // Aluminum: vinyl-applied only for Global
    if (ALUMINUM_SUBSTRATE_IDS.includes(substrate)) {
      allowed = allowed.filter(g => g === 'vinyl-applied');
    }
    // Black substrates: no direct print (cut-only)
    if (BLACK_SUBSTRATES.includes(substrate)) {
      allowed = allowed.filter(g => !['direct-1st','direct-2nd'].includes(g));
    }
    // Banner/flexible substrates: direct print only
    if (BANNER_SUBSTRATE_IDS.includes(substrate)) {
      allowed = allowed.filter(g => g === 'direct-1st');
    }
    // SCM / Magnetic: direct print only
    if (['SCM_3MM_WHITE','MAGNETIC_.015IN_WHITE','MAGNETIC_.030IN_WHITE'].includes(substrate)) {
      allowed = allowed.filter(g => g === 'direct-1st');
    }
    // 2nd surface only available on clear substrates
    if (!CLEAR_SUBSTRATE_IDS.includes(substrate)) {
      allowed = allowed.filter(g => g !== 'direct-2nd');
    }
    // All flexible/roll substrates: vinyl-applied is never an option —
    // you print directly on the material; there's no rigid base to overlay vinyl onto
    const sub = (SUBSTRATES || []).find(s => s.id === substrate);
    if (sub && sub.matType === 'flexible') {
      allowed = allowed.filter(g => g !== 'vinyl-applied');
    }
  }

  return GRAPHIC_METHODS.filter(g => allowed.includes(g.id));
}

/**
 * Returns true if 2nd-surface print should be available.
 * Only available on clear substrates.
 */
export function is2ndSurfaceAvailable(substrate: string): boolean {
  return CLEAR_SUBSTRATE_IDS.includes(substrate)
}

/**
 * Returns true if white ink options should be shown.
 */
export function isWhiteInkAvailable(substrate: string): boolean {
  if (!substrate) return false
  if (CLEAR_SUBSTRATE_IDS.includes(substrate)) return true
  if (BLACK_SUBSTRATES.includes(substrate)) return true
  const sub = SUBSTRATES.find((s) => s.id === substrate)
  if (sub && sub.matType === 'ada') return false
  return false
}

/**
 * Returns the allowed vinyl type objects for the current category + graphic method
 * (+ optional substrate for combo overrides).
 */
export function getAvailableVinylTypes(
  category: string,
  graphicMethod: string,
  substrate: string = '',
): MockCatalogItem[] {
  if (graphicMethod !== 'vinyl-applied' && graphicMethod !== 'cut-vinyl') return [];
  if (substrate) {
    const comboKey = `${category}|${substrate}|${graphicMethod}`;
    const comboAllowed = VINYL_COMBO_OVERRIDES[comboKey];
    if (comboAllowed) {
      return VINYL_TYPES.filter((v) => comboAllowed.includes(v.id));
    }
  }
  const allowed = CATEGORY_VINYL_TYPES[category];
  if (!allowed) return VINYL_TYPES; // no restriction for unmapped categories
  return VINYL_TYPES.filter(v => allowed.includes(v.id));
}

/**
 * Returns the allowed laminate objects for the current substrate + vinyl type.
 * substrate: substrate ID; vinylType: vinyl ID or '' for direct-print.
 */
export function getAvailableLaminates(
  substrate: string,
  vinylType: string,
  graphicMethod: string,
  category: string
): MockCatalogItem[] {
  const all = LAMINATES || [];

  // Floor laminate is only valid for floor graphics; hide it everywhere else
  // ('other' / no category = unrestricted, so show it there)
  const baseAll = (category && category !== 'other' && category !== 'floor-graphics')
    ? all.filter(l => l.id !== 'LAM_FLOOR')
    : all;

  // ADA: no laminate
  const sub = (SUBSTRATES || []).find(s => s.id === substrate);
  if (sub && sub.matType === 'ada') return baseAll.filter(l => l.id === 'none');

  // Banner / static cling substrates: no laminate
  if (NO_LAM_SUBSTRATE_IDS.includes(substrate)) {
    return baseAll.filter(l => l.id === 'none');
  }

  // Floor vinyl: floor laminate is the ONLY valid choice (SOP 6.23.2.1 — required for safety).
  // Pull LAM_FLOOR directly from the unfiltered list so the category guard never removes it.
  if (substrate === 'VINYL_CAL_FLOOR') {
    const floorLam = (all || []).find(l => l.id === 'LAM_FLOOR');
    return floorLam ? [floorLam] : [];
  }

  // Vinyl type drives lam selection when vinyl-applied
  if (vinylType && graphicMethod === 'vinyl-applied') {
    // No-lam vinyls
    if (NO_LAM_VINYL_IDS.includes(vinylType)) {
      return baseAll.filter(l => l.id === 'none');
    }
    // Mapped vinyl → lam restriction
    if (VINYL_LAM_MAP[vinylType]) {
      const allowed = [...VINYL_LAM_MAP[vinylType], 'none'];
      return baseAll.filter(l => allowed.includes(l.id));
    }
    return baseAll; // unmapped vinyl: no restriction
  }

  // Rigid substrate + direct print (no vinyl): liquid lam only per user rule
  if (graphicMethod && ['direct-1st','direct-2nd'].includes(graphicMethod) && !vinylType) {
    // Liquid-lam-only boards (ACM wall boards, SCM)
    if (LIQUID_LAM_ONLY_SUBSTRATE_IDS.includes(substrate)) {
      return baseAll.filter(l => LIQUID_LAM_IDS.includes(l.id) || l.id === 'none');
    }
    // Other rigid direct-print: liquid + dry-erase film only
    if (sub && sub.matType === 'rigid') {
      return baseAll.filter(l => LIQUID_LAM_IDS.includes(l.id) || l.id === 'none');
    }
  }

  return baseAll;
}

/**
 * Auto-infer the taping/application requirement from the current build.
 * Rules (in priority order):
 *   1. Aluminum + vinyl-applied → APP MASK  (masking tape over the overlay before CNC routing)
 *   2. RTA, vinyl-applied (non-aluminum), cut-vinyl → APP TAPE  (transfer tape for application)
 *   3. Otherwise → '' (no taping needed)
 */
export function inferTaping(substrate: string, graphicMethod: string): string {
  if (ALUMINUM_SUBSTRATE_IDS.includes(substrate) && graphicMethod === 'vinyl-applied') {
    return 'APP MASK';
  }
  if (['rta', 'vinyl-applied', 'cut-vinyl'].includes(graphicMethod)) {
    return 'APP TAPE';
  }
  return '';
}

// Category → default substrate mapping
export const CATEGORY_DEFAULTS: Record<
  string,
  { substrate: string; graphic: string; vinyl: string; laminate: string; taping: string }
> = {
    'post-mounted-longterm': { substrate: 'ALUMINUM_.080IN_WHITE', graphic: 'vinyl-applied', vinyl: 'VINYL_REF_WHITE_EG_NON-PRISMATIC', laminate: 'LAM_CAL-GLOSS', taping: 'APP MASK' },
    'post-mounted-temp': { substrate: 'ACM_3MM_WHITE', graphic: 'direct-1st', vinyl: '', laminate: 'none', taping: '' },
    'wall-fence-exterior': { substrate: 'ALUMINUM_.080IN_WHITE', graphic: 'vinyl-applied', vinyl: 'VINYL_CAL_INTERMEDIATE', laminate: 'LAM_CAL-GLOSS', taping: 'APP MASK' },
    'wall-boards-interior': { substrate: 'ACM_3MM_WHITE', graphic: 'direct-1st', vinyl: '', laminate: 'none', taping: '' },
    'a-boards-yard': { substrate: 'COROPLAST_4MM_WHITE', graphic: 'direct-1st', vinyl: '', laminate: 'none', taping: '' },
    'dimensional': { substrate: 'ACRYLIC_0.25IN_CLEAR', graphic: 'direct-2nd', vinyl: '', laminate: 'none', taping: '' },
    'banners': { substrate: 'BANNER_13OZ_SCRIM_MATTE', graphic: 'direct-1st', vinyl: '', laminate: 'none', taping: '' },
    'vinyl-wall': { substrate: 'VINYL_CAST', graphic: 'direct-1st', vinyl: '', laminate: 'LAM_CAST-MATTE', taping: '' },
    'vinyl-window': { substrate: 'VINYL_OPTICALLY_CLEAR_CAL', graphic: 'direct-1st', vinyl: '', laminate: 'LAMINATE_OPTICALLY_CLEAR', taping: '' },
    'vinyl-floor': { substrate: 'VINYL_CAL_FLOOR', graphic: 'direct-1st', vinyl: '', laminate: 'LAM_FLOOR', taping: '' },
    'decals-stickers': { substrate: 'VINYL_CAL_INTERMEDIATE', graphic: 'direct-1st', vinyl: '', laminate: 'none', taping: 'APP TAPE / MASK' },
    'magnets': { substrate: 'MAGNETIC_.030IN_WHITE', graphic: 'direct-1st', vinyl: '', laminate: 'none', taping: '' },
    'folded-displays': { substrate: 'HONEYCOMB_BOARD_0.5IN_W-K-W', graphic: 'direct-1st', vinyl: '', laminate: 'none', taping: '' },
    'ada-specialty': { substrate: 'ACRYLIC_1-8IN_COLOR_ROWMARK', graphic: 'none', vinyl: '', laminate: 'none', taping: '' },
    'posters-prints': { substrate: 'PAPER_POSTER_.011IN_SATIN', graphic: 'direct-1st', vinyl: '', laminate: 'none', taping: '' },
    'pylons-lightboxes': { substrate: 'POLYCARBONATE_0.185IN_WHITE_RL', graphic: 'direct-1st', vinyl: '', laminate: 'none', taping: '' },
    'other': { substrate: '', graphic: 'none', vinyl: '', laminate: 'none', taping: '' }
};

// ─── SIGN BUILDER EDUCATION GUIDE CONTENT ──────────────
export const SIGN_GUIDE_CONTENT: SignGuideContent = {
    sections: {
        1: {
            title: "Choosing a Category",
            overview: "Start by picking the type of sign you're building. This determines which substrates, graphic methods, and finishes are recommended. If you're unsure, browse the real-world examples below to find the best match.",
            categoryExamples: {
                'post-mounted-longterm': { examples: ['Street address ID signs', 'Highway & DOT road signs', 'Parking lot identification panels', 'Monument sign faces', 'Post & panel directional signs', 'Property entrance signage'], note: 'Aluminum substrate with reflective or cast vinyl + laminate. Built to last 5-10+ years in all weather.' },
                'post-mounted-temp': { examples: ['Real estate yard signs', 'Political campaign signs', 'Open house / sale event signs', 'Construction site notices', 'Garage sale & community event signs'], note: 'Coroplast or ACM with direct print. Cost-effective, 3-12 months outdoor life.' },
                'wall-fence-exterior': { examples: ['Building-mounted business ID signs', 'Fence-mounted construction banners', 'Exterior wayfinding arrows', 'Loading dock and safety signs', 'Outdoor menu boards', 'Exterior ADA / code-required signs'], note: 'Aluminum or ACM with vinyl + laminate for long-term. Direct print for temporary.' },
                'wall-boards-interior': { examples: ['Lobby directory boards', 'Interior wayfinding signs', 'Conference room name plates', 'Office hour boards', 'Retail aisle markers', 'Interior safety & compliance signs', 'Reception area branding'], note: 'ACM, PVC, or acrylic. Direct print is common for indoor use. Laminate optional.' },
                'a-boards-yard': { examples: ['Sidewalk A-frame signs (restaurant specials, retail promos)', 'Realtor open house A-frames', 'Event directional A-frames', 'Wire stake yard signs'], note: 'A-board inserts must stay at 24" for hardware fit. Coroplast or polystyrene.' },
                'dimensional': { examples: ['3D lobby logos (raised lettering)', 'Acrylic standoff-mounted letters', 'PVC dimensional letters', 'Layered acrylic signs', 'Metal-faced dimensional logos'], note: 'Acrylic or thick PVC. Cast vinyl required for contoured surfaces. 2nd-surface print for clear substrates.' },
                'banners': { examples: ['Outdoor building banners (grand opening, sale)', 'Indoor event banners', 'Trade show backdrop banners', 'Pole banners (street light / parking lot)', 'Retractable banner stand inserts', 'Fence mesh banners'], note: '13oz scrim vinyl. Grommets and hems for outdoor. Direct print standard.' },
                'vinyl-wall': { examples: ['Full wall murals (office, lobby, restaurant)', 'Branded accent walls', 'Wall-wrap graphics (hallways, stairwells)', 'Textured wall vinyl for brick or cinderblock', 'Environmental graphics for hospitals, schools'], note: 'Cast vinyl for textured walls, cal for smooth. Laminate protects in high-traffic areas.' },
                'vinyl-window': { examples: ['Storefront window graphics (hours, logo, promo)', 'Frosted/etched privacy film', 'Perforated window vinyl (see-through from inside)', 'Window clings (seasonal, reusable)', 'Full window wraps', 'Glass door safety strips'], note: 'Optically clear for see-through, perforated for one-way vision, cling for reusable.' },
                'vinyl-floor': { examples: ['Retail floor ads & promotions', 'Wayfinding floor arrows (hospitals, airports)', 'Social distancing markers', 'Trade show floor graphics', 'Exterior walkway graphics (Alumigraphics)'], note: 'Floor laminate required for slip resistance. Alumigraphics for exterior walkways.' },
                'decals-stickers': { examples: ['Product labels & stickers', 'Bumper stickers', 'Window decals (logo, open/closed)', 'Equipment & asset ID labels', 'Die-cut logo decals', 'QR code stickers'], note: 'Cal intermediate vinyl with app tape/mask. Laminate for outdoor durability.' },
                'magnets': { examples: ['Vehicle door magnets (service companies)', 'Fridge magnets (promotional giveaways)', 'Magnetic name badges', 'Warehouse rack magnets (inventory labels)'], note: 'Magnetic substrate with direct print. .030" for vehicle, .015" for fridge.' },
                'folded-displays': { examples: ['Tabletop tent cards (restaurant menus, table numbers)', 'Counter display stands', 'Point-of-purchase (POP) displays', 'Tri-fold brochure holders', 'Product display easels'], note: 'Honeycomb, foamcore, or polystyrene. Scored and folded. Direct print.' },
                'other': { examples: ['Custom items not listed above', 'Multi-component projects', 'Specialty materials or processes'], note: 'Describe the product in the Product Description field. The estimating team will determine the best approach.' }
            },
            decisionHelper: {
                question: "Where will this sign be used?",
                options: [
                    { label: "Outdoors — permanent (5+ yrs)", ids: ['post-mounted-longterm', 'wall-fence-exterior', 'dimensional'], tip: "Use durable substrates like aluminum or acrylic with vinyl + laminate graphics." },
                    { label: "Outdoors — temporary (under 1 yr)", ids: ['post-mounted-temp', 'a-boards-yard', 'banners'], tip: "Lighter materials like Coroplast or ACM work well. Direct print is usually fine." },
                    { label: "Indoors only", ids: ['wall-boards-interior', 'vinyl-wall', 'vinyl-window', 'vinyl-floor'], tip: "More material options available. Laminate is optional for indoor signs." }
                ]
            },
            glossary: [
                { term: "Substrate", def: "The base material on which graphics are printed or applied." },
                { term: "Signage", def: "Graphics and text used to communicate information in public or commercial spaces." },
                { term: "Wayfinding", def: "System of signage designed to guide people through a space." },
                { term: "Monument Sign", def: "Freestanding ground-level sign structure typically built from masonry or panels." },
                { term: "Pylon Sign", def: "Tall freestanding sign mounted on poles used for visibility from a distance." },
                { term: "Channel Letter", def: "3D illuminated letters typically used on storefront signage." },
                { term: "Dimensional Letter", def: "Raised lettering fabricated from acrylic, metal, or foam." },
                { term: "Lightbox", def: "Sign cabinet that contains internal lighting to illuminate graphics." },
                { term: "ADA Signage", def: "Accessibility signage meeting Americans with Disabilities Act requirements." },
                { term: "Sign Program", def: "Coordinated signage plan covering multiple locations or a large facility." },
                { term: "Brand Standards", def: "Guidelines defining consistent visual presentation for a company's signage." },
                { term: "Custom Sign", def: "Sign built to unique specifications rather than from a catalog SKU." },
                { term: "SKU", def: "Stock Keeping Unit used to track standardized sign products." }
            ]
        },
        2: {
            title: "Substrate & Size",
            overview: "The substrate is the foundation of your sign — the physical material that carries the graphics. Heavier, more rigid substrates last longer outdoors. Dimensions affect material efficiency, so check the tips for optimal sizing.\n\nRIGID substrates come in fixed sheets (e.g. 48\u00D796\"). FLEXIBLE substrates come on rolls with a fixed width but unlimited length. For roll materials, enter the finished W and H as the customer sees it — operations will rotate and nest the print to optimize material. As long as one dimension fits within the roll width, the product can be produced.",
            decisionHelper: {
                question: "How long does this sign need to last?",
                options: [
                    { label: "Short-term (under 1 year)", ids: ['COROPLAST_4MM_WHITE', 'POLYSTYRENE_.030IN_WHITE', 'POLYSTYRENE_.060IN_WHITE', 'FOAMCORE', 'BUBBLE-X_4MM_WHITE', 'BUBBLE-X_5MM_WHITE'], tip: "Lightweight, affordable materials. Great for events, promotions, and temp signage." },
                    { label: "Medium-term (1–5 years)", ids: ['ACM_3MM_WHITE', 'PVC_3MM', 'SCM_3MM_WHITE', 'CONVERD'], tip: "Mid-range durability. Good for semi-permanent indoor/outdoor use." },
                    { label: "Long-term (5+ years)", ids: ['ALUMINUM_.080IN_WHITE', 'ALUMINUM_.040IN_WHITE', 'ACRYLIC_0.25IN_CLEAR', 'ACRYLIC_0.25IN_WHITE'], tip: "Premium materials built to withstand years of weather and UV exposure." }
                ]
            },
            glossary: [
                { term: "Substrate", def: "The base material on which graphics are printed or applied." },
                { term: "ACM (Aluminum Composite Material)", def: "Rigid panel made of aluminum sheets bonded to a plastic core." },
                { term: "Expanded PVC", def: "Lightweight plastic sheet commonly used for indoor signage." },
                { term: "Acrylic", def: "Clear or colored plastic sheet often used for premium signage and dimensional letters." },
                { term: "Polycarbonate", def: "Highly impact-resistant plastic used where durability is critical." },
                { term: "Coroplast", def: "Corrugated plastic sheet commonly used for yard and temporary signage." },
                { term: "HDU (High Density Urethane)", def: "Dense foam material used for carved dimensional signage." },
                { term: "Banner Vinyl", def: "Flexible PVC material used for promotional banners." },
                { term: "Backlit Film", def: "Translucent print material used in illuminated signs." },
                { term: "Mounting", def: "Adhering a printed graphic to a rigid backing panel." },
                { term: "Direct-to-Substrate Printing", def: "Printing directly onto rigid materials like acrylic, PVC, or aluminum." }
            ]
        },
        3: {
            title: "Graphic Layer",
            overview: "This is how your design gets onto the sign. Direct print is faster and cheaper but fades outdoors. Vinyl applied with laminate is the gold standard for outdoor durability. RTA and cut vinyl are for simpler single-color designs.\n\nNote: When vinyl is applied ON TOP of a rigid board (ACM, aluminum, etc.), any vinyl + laminate combination is allowed. The Cast/Cal matching restriction only applies when vinyl is your BASE SUBSTRATE (selected in Section 2).",
            decisionHelper: {
                question: "Does the customer need night visibility?",
                options: [
                    { label: "Yes — reflective for headlights", ids: ['VINYL_REF_WHITE_EG_NON-PRISMATIC', 'VINYL_REF_WHITE_HIP'], tip: "Use reflective vinyl. Required for road signs, parking areas, and DOT applications." },
                    { label: "No — standard daytime viewing", ids: ['VINYL_CAL_INTERMEDIATE', 'VINYL_CAST'], tip: "Standard vinyl or direct print will work. Choose based on durability needs." }
                ]
            },
            glossary: [
                { term: "Cast Vinyl", def: "Premium vinyl for curves, wraps, and long-term exterior. Pairs ONLY with Cast laminate. 7-12 year outdoor durability." },
                { term: "Calendered (Cal) Vinyl", def: "Standard vinyl for flat surfaces. Cost-effective, 3-5 year outdoor life. Pairs ONLY with Cal laminate." },
                { term: "Reflective Vinyl", def: "Vinyl that reflects light for better night visibility." },
                { term: "UV Printing", def: "Inkjet printing using ultraviolet light to instantly cure ink on rigid or flexible materials." },
                { term: "Latex Printing", def: "Water-based printing technology known for environmental safety and durability." },
                { term: "Eco-Solvent Printing", def: "Solvent ink printing that offers durable outdoor graphics." },
                { term: "Bleed", def: "Extra printed area beyond the trim edge to ensure color reaches the edge after cutting." },
                { term: "Safe Area", def: "The interior margin where important text and graphics should stay to avoid being cut off." },
                { term: "CMYK", def: "Color model used for printing: Cyan, Magenta, Yellow, Black." },
                { term: "Pantone (PMS)", def: "Standardized color matching system used for consistent brand color reproduction." },
                { term: "Resolution (DPI)", def: "Dots per inch; measurement describing print image detail." },
                { term: "Vector Graphic", def: "Artwork made from mathematical paths allowing infinite scaling without loss of quality." },
                { term: "Raster Image", def: "Pixel-based image that loses quality when enlarged beyond its native resolution." },
                { term: "Contour Cutting", def: "Cutting printed graphics along a custom shape instead of straight edges." },
                { term: "Weeding", def: "Removing excess vinyl after cutting to leave only the intended graphic." },
                { term: "Preflight", def: "Quality check of design files before printing to ensure correct fonts, colors, and resolution." }
            ]
        },
        4: {
            title: "Laminate & Coating",
            overview: "Laminate is a clear protective film applied over the printed graphic. It shields against UV fading, scratches, and weather. For outdoor signs, laminate can double the graphic's lifespan. Gloss adds vibrancy; matte gives a professional, non-glare finish.\n\nCRITICAL: When your base substrate (Section 2) is a Cast or Cal vinyl, the laminate MUST match — Cast laminate with Cast vinyl, Cal laminate with Cal vinyl. Mixing causes delamination because the materials shrink and expand at different rates. This restriction does NOT apply when vinyl is applied on top of a rigid board.",
            decisionHelper: {
                question: "Is UV protection important for this sign?",
                options: [
                    { label: "Yes — outdoor or sun-exposed", ids: ['LAM_CAL-GLOSS', 'LAM_CAL-MATTE', 'LAM_CAST-GLOSS', 'LAM_CAST-MATTE'], tip: "Film laminates provide real UV protection. Match to your vinyl: Cal lam with Cal vinyl, Cast lam with Cast vinyl." },
                    { label: "No — indoor or short-term", ids: ['none'], tip: "Skipping laminate saves time and cost for indoor or temporary signs." }
                ]
            },
            glossary: [
                { term: "Overlaminate", def: "A film applied over graphics for protection from UV and abrasion." },
                { term: "Material Finish", def: "The surface quality of sign materials, such as matte, gloss, or textured." },
                { term: "Cast vs Cal Matching", def: "Cast and Calendered materials have different shrinkage rates. Cast laminate must be used with Cast vinyl, and Cal laminate with Cal vinyl. Mixing causes delamination, bubbling, and premature graphic failure." },
                { term: "Color Matching", def: "Process of reproducing specific brand colors accurately across materials and print methods." },
                { term: "Proof", def: "Preliminary version of artwork used for customer review before production." },
                { term: "Production Proof", def: "Final proof that reflects the exact layout, materials, and specs used for fabrication." }
            ]
        },
        5: {
            title: "Finishing & Extras",
            overview: "Final touches that affect installation and durability. Application tape holds vinyl elements in position during install. Hardware determines how the sign mounts. For banners, grommets and hems add structural strength.",
            decisionHelper: {
                question: "How will this sign be installed?",
                options: [
                    { label: "Post-mounted or freestanding", ids: [], tip: "Hardware specs are usually provided by the installer. Include mounting notes if known." },
                    { label: "Wall or fence mounted", ids: [], tip: "Standoffs give a professional floating look. Flush mount is simpler and cheaper." },
                    { label: "Hung or suspended (banners)", ids: [], tip: "Grommets every 24\" is standard. Add hem on all sides for outdoor wind resistance." }
                ]
            },
            glossary: [
                { term: "Grommet", def: "A metal ring inserted into banner corners for hanging." },
                { term: "Banner Hem", def: "Reinforced edge of a banner for strength and durability." },
                { term: "French Cleat", def: "Mounting system using interlocking angled pieces for secure wall mounting." },
                { term: "Standoff Mount", def: "Hardware used to offset a sign panel slightly away from the wall for a floating look." },
                { term: "Stud Mount", def: "Threaded rods used to mount dimensional letters to a wall." },
                { term: "Flush Mount", def: "Installation method where a sign sits flat against the mounting surface." },
                { term: "Sign Cabinet", def: "Enclosure structure housing illuminated sign faces and lighting." },
                { term: "Routing", def: "CNC cutting process used to shape rigid sign materials." },
                { term: "Laser Cutting", def: "Precision cutting method using a laser beam for materials like acrylic." },
                { term: "Thermoforming", def: "Heating plastic until flexible then forming it into shapes." },
                { term: "Edge Polishing", def: "Finishing process making acrylic edges clear and smooth." },
                { term: "Trim Cap", def: "Flexible border material applied to the edges of channel letters." },
                { term: "Return", def: "Side wall of a channel letter structure." },
                { term: "Backer Panel", def: "Panel used to mount letters or graphics before installation." },
                { term: "Site Survey", def: "On-site evaluation of installation conditions and measurements." },
                { term: "Wind Load", def: "Engineering measure of how much wind force a sign structure must withstand." },
                { term: "Raceway", def: "Metal enclosure used to house electrical wiring for illuminated signs." },
                { term: "LED Module", def: "Lighting component used to illuminate modern signage." },
                { term: "Halo Lighting", def: "Illumination style where light shines behind letters creating a glow effect." },
                { term: "Turnaround Time", def: "Estimated time required to produce and deliver a sign." },
                { term: "Punch List", def: "List of final corrections needed before a project is complete." },
                { term: "Permit", def: "Official authorization required from municipalities for certain signage installations." }
            ]
        }
    },
    materials: {
        // ── Substrates ──
        'ALUMINUM_.080IN_WHITE': { name: 'Aluminum .080" White', what: 'Premium rigid aluminum sheet, 0.080" thick. The workhorse for permanent exterior signage. Raw sheet: 48×96".', durability: '10+ years outdoor', bestFor: 'Permanent exterior signs — post-mounted, wall-mounted, wayfinding', avoid: 'Not cost-effective for temporary or indoor-only use', sopRef: 'SOP 6.12 — For Global customers, use vinyl + laminate (no direct print on aluminum)' },
        'ALUMINUM_.080IN_MILL': { name: 'Aluminum .080" Mill Finish', what: 'Natural aluminum finish sheet, 0.080" thick. Raw sheet: 48×96".', durability: '10+ years outdoor', bestFor: 'Permanent exterior signs with natural metal look', avoid: 'Not cost-effective for temporary or indoor-only use', sopRef: '' },
        'ALUMINUM_.040IN_WHITE': { name: 'Aluminum .040" White', what: 'Thinner aluminum sheet. More affordable but less rigid than .080". Raw sheet: 48×96".', durability: '7-10 years outdoor', bestFor: 'Smaller exterior signs, interior signs needing premium look', avoid: 'Large unsupported spans (will flex)', sopRef: '' },
        'ACM_3MM_WHITE': { name: 'ACM 3mm White', what: 'Aluminum Composite Material — lightweight sandwich of aluminum faces with plastic core. Raw sheets: 48×96" (stock), 60×120" (special order).', durability: '3-5 years outdoor', bestFor: 'Semi-permanent exterior, interior feature walls, real estate signs', avoid: 'Permanent outdoor without laminate protection', sopRef: '' },
        'PVC_3MM_WHITE_INDOOR': { name: 'PVC 3mm White', what: 'Rigid plastic board — smooth, paintable, and versatile. Raw sheet: 48×96".', durability: '3-5 years outdoor, 10+ indoor', bestFor: 'Interior signs, dimensional letters, displays', avoid: 'Dimensional letters for Global — use acrylic per SOP 6.19.3', sopRef: 'SOP 6.19.3 — Use acrylic instead of PVC for dimensional elements' },
        'COROPLAST_4MM_WHITE': { name: 'Coroplast 4mm', what: 'Corrugated plastic — lightweight, waterproof, and very affordable. Raw sheets: 48×96" (stock), 60×120" (special order).', durability: 'Under 1 year outdoor', bestFor: 'Yard signs, event signage, temporary promotions', avoid: 'Permanent exterior — not suitable per SOP 6.22.2', sopRef: 'SOP 6.22.2 — Not approved for long-term exterior use' },
        'ACRYLIC_0.25IN_CLEAR': { name: 'Acrylic .25" Clear', what: 'Transparent thermoplastic — glass-like clarity with impact resistance.', durability: '10+ years', bestFor: 'Dimensional letters, backlit signs, premium interior displays', avoid: 'Direct surface exposure to harsh weather without protection', sopRef: 'SOP 6.19.3 — Preferred over PVC for dimensional elements' },
        'ACRYLIC_0.25IN_WHITE': { name: 'Acrylic .25" White', what: 'Opaque white acrylic — clean, modern look with excellent durability.', durability: '10+ years', bestFor: 'Dimensional letters, ADA signs, interior feature signs', avoid: '', sopRef: '' },
        'SCM_3MM_WHITE': { name: 'SCM 3mm White', what: 'Styrene composite material — smooth, lightweight, good print surface.', durability: '2-3 years indoor', bestFor: 'Interior signs, POP displays, menu boards', avoid: 'Outdoor use — not weather resistant', sopRef: '' },
        'POLYSTYRENE_.030IN_WHITE': { name: 'Polystyrene .030"', what: 'Very thin, lightweight plastic sheet — the most affordable option.', durability: 'Short-term only', bestFor: 'Inserts, temporary displays, packaging', avoid: 'Any outdoor application', sopRef: '' },
        'POLYSTYRENE_.060IN_WHITE': { name: 'Polystyrene .060"', what: 'Slightly thicker polystyrene — still lightweight and budget-friendly.', durability: 'Short-term only', bestFor: 'Counter cards, table tents, temporary indoor signs', avoid: 'Any outdoor application', sopRef: '' },
        'HONEYCOMB_BOARD_0.5IN_W-K-W': { name: 'Honeycomb Board', what: 'Lightweight structural board with honeycomb interior — strong for its weight.', durability: '1-2 years indoor', bestFor: 'Large format displays, folded structures, trade show graphics', avoid: 'Outdoor use, humid environments', sopRef: 'SOP 6.2.1 — Reduce 48×96 to 46.5×94 or 47×94 for efficiency' },
        'FOAMCORE_0.1875IN_WHITE_ECONOM': { name: 'Foamcore', what: 'Foam center with paper faces — very light, easy to cut.', durability: 'Indoor/event use only', bestFor: 'Presentations, photo mounts, temporary displays', avoid: 'Any exterior or high-traffic application', sopRef: 'SOP 6.2.1 — Reduce 48×96 to 46.5×94 or 47×94' },
        'BANNER_13OZ_SCRIM_MATTE': { name: 'Banner 13oz Scrim Matte', what: 'Standard weight vinyl banner with matte finish — the industry go-to.', durability: '1-3 years outdoor', bestFor: 'Event banners, retail promotions, construction site wraps', avoid: 'Permanent installations — use rigid substrate instead', sopRef: '' },
        'MAGNETIC_.015IN_WHITE': { name: 'Magnetic .015"', what: 'Thin flexible magnet sheet — sticks to metal surfaces.', durability: '1-2 years', bestFor: 'Vehicle door magnets, fridge magnets, removable signage', avoid: 'Non-metal surfaces, permanent outdoor', sopRef: '' },
        'MAGNETIC_.030IN_WHITE': { name: 'Magnetic .030"', what: 'Thicker magnetic sheet — stronger hold, more durable.', durability: '2-3 years', bestFor: 'Vehicle magnets, warehouse labels, reusable signage', avoid: 'Non-metal surfaces', sopRef: '' },
        'CONVERD_3MM_WHITE': { name: 'Converd', what: 'Recycled fiber board — eco-friendly alternative to foam and plastic substrates.', durability: '1-2 years indoor', bestFor: 'Eco-conscious displays, POP, temporary indoor signage', avoid: 'Outdoor, humid environments', sopRef: 'SOP 6.2.1 — Reduce 48×96 to 46.5×94 or 47×94' },
        'BUBBLE-X_4MM_WHITE': { name: 'Bubble-X', what: 'Lightweight corrugated polypropylene — similar to Coroplast but smoother.', durability: 'Under 1 year outdoor', bestFor: 'Temporary signs, real estate riders, event directionals', avoid: 'Permanent exterior use', sopRef: '' },
        // ── Vinyl Types ──
        'VINYL_CAL_INTERMEDIATE': { name: 'Calendered Intermediate', what: 'Standard vinyl for flat applications — good color range, cost-effective.', durability: '3-5 years outdoor', bestFor: 'Flat signs, wall graphics, window lettering', avoid: 'Compound curves or vehicle wraps (use cast vinyl)', sopRef: '' },
        'VINYL_CAST': { name: 'Cast Vinyl', what: 'Premium vinyl that conforms to curves and textured surfaces without lifting.', durability: '7-12 years outdoor', bestFor: 'Vehicle wraps, curved surfaces, long-term exterior graphics', avoid: 'Flat simple signs where cal vinyl would suffice (saves cost)', sopRef: 'SOP 6.5 — Cast vinyl required for compound curves and long-term exterior' },
        'VINYL_REF_WHITE_EG_NON-PRISMATIC': { name: 'Reflective EG Non-Prismatic', what: 'Engineer-grade reflective vinyl — glows when hit by headlights at night.', durability: '7-10 years outdoor', bestFor: 'Road signs, parking signs, safety signs, DOT-compliant signage', avoid: 'Indoor-only signs (adds unnecessary cost)', sopRef: 'SOP 6.5' },
        'VINYL_REF_WHITE_HIP': { name: 'Reflective HIP', what: 'High-Intensity Prismatic — brighter than EG, visible from greater distances.', durability: '10+ years outdoor', bestFor: 'Highway signs, high-speed road signs, critical safety signage', avoid: 'Standard commercial signage (EG is sufficient and cheaper)', sopRef: 'SOP 6.5' },
        'VINYL_CAL_FLOOR': { name: 'Calendered Floor Vinyl', what: 'Vinyl formulated for floor applications with anti-slip overlaminate.', durability: '6-12 months high traffic', bestFor: 'Floor graphics, wayfinding paths, retail floor promotions', avoid: 'Wall or sign applications (use standard cal)', sopRef: '' },
        'VINYL_OPTICALLY_CLEAR_CAL': { name: 'Optically Clear Vinyl', what: 'Crystal-clear vinyl for see-through applications on glass.', durability: '3-5 years', bestFor: 'Window graphics, glass partitions, storefront branding', avoid: 'Opaque sign applications', sopRef: '' },
        'VINYL_WALL_TEXTURED_SURFACE': { name: 'Wall Textured Surface', what: 'Conformable vinyl designed to adhere to textured walls and painted surfaces.', durability: '3-5 years indoor', bestFor: 'Interior murals, textured drywall graphics, brick/concrete surfaces', avoid: 'Smooth surfaces (standard vinyl is cheaper and works fine)', sopRef: '' },
        'VINYL_PERFORATED_STOREFRONT': { name: 'Perforated Storefront', what: 'Tiny holes let light through from inside while showing graphics outside.', durability: '2-3 years outdoor', bestFor: 'Store window advertising that still lets light in', avoid: 'Signs needing full opacity or night visibility', sopRef: '' },
        // ── Laminates ──
        'LAM_CAL-GLOSS': { name: 'Cal Gloss Laminate', what: 'Glossy calendered overlaminate — adds vibrant shine and UV protection.', durability: '5-7 years outdoor', bestFor: 'Outdoor vinyl graphics, retail signage, photo-quality prints', avoid: 'High-glare environments where matte would be better', sopRef: 'SOP 6.8' },
        'LAM_CAL-MATTE': { name: 'Cal Matte Laminate', what: 'Matte calendered overlaminate — non-glare finish with UV protection.', durability: '5-7 years outdoor', bestFor: 'Professional/corporate signs, wayfinding, anywhere glare is a concern', avoid: '', sopRef: 'SOP 6.8' },
        'LAM_CAST-GLOSS': { name: 'Cast Gloss Laminate', what: 'Premium glossy laminate that conforms to curves — pairs with cast vinyl.', durability: '10+ years outdoor', bestFor: 'Vehicle wraps, curved surfaces, premium long-term graphics', avoid: 'Flat surfaces where cal laminate suffices (saves cost)', sopRef: 'SOP 6.8' },
        'LAM_CAST-MATTE': { name: 'Cast Matte Laminate', what: 'Premium matte laminate for curves — pairs with cast vinyl.', durability: '10+ years outdoor', bestFor: 'Premium matte vehicle wraps, curved corporate signage', avoid: 'Flat surfaces where cal laminate suffices', sopRef: 'SOP 6.8' },
        'LAM_ANTI-GRAFFITI': { name: 'Anti-Graffiti Laminate', what: 'Specialty film that allows graffiti to be wiped off without damaging graphics.', durability: '5-7 years outdoor', bestFor: 'Public-facing signs in urban areas, transit stops, parks', avoid: 'Low-vandalism-risk areas (adds cost)', sopRef: '' },
        'LAM_DRY-ERASE': { name: 'Dry-Erase Laminate', what: 'Allows writing with dry-erase markers directly on the laminated surface.', durability: '3-5 years indoor', bestFor: 'Menu boards, scheduling boards, interactive displays', avoid: 'Outdoor use', sopRef: '' },
        'LAM_FLOOR': { name: 'Floor Laminate', what: 'Anti-slip textured overlaminate — required for any floor graphic application.', durability: '6-12 months high traffic', bestFor: 'Floor graphics, walkway signage, retail floor displays', avoid: 'Wall or sign applications', sopRef: '' },
        'LAMINATE_OPTICALLY_CLEAR': { name: 'Optically Clear Laminate', what: 'Crystal-clear protective film for window and glass applications.', durability: '3-5 years', bestFor: 'Window graphics, glass displays, see-through applications', avoid: 'Standard sign applications', sopRef: '' },
        'LIQUID_DRY-ERASE_LAM': { name: 'Liquid Dry-Erase Lam', what: 'Spray-on dry-erase coating. Cheaper than film but less durable.', durability: 'Limited', bestFor: 'Budget dry-erase boards, temporary interactive surfaces', avoid: 'Outdoor use or heavy daily use', sopRef: 'SOP 6.8 — Liquid laminates do NOT provide UV protection or added durability' },
        'LIQUID_GLOSS_LAM': { name: 'Liquid Gloss Lam', what: 'Spray-on gloss coating. NOT approved for Global production.', durability: 'Minimal protection', bestFor: 'Local production only — quick turnaround indoor pieces', avoid: 'Global production (not approved), outdoor use', sopRef: 'SOP 6.8.2 — Not approved for Global production' }
    }
};

// ─── SOP SECTIONS DATA (for expandable SOP references in Sign Builder UI) ───
/** Ported from rukh-pd-queue/origin/frontend/data/sign-builder-data.js L889–1016 */
export const SOP_SECTIONS: Record<string, SopSectionEntry> = {
  '6.2.1': {
    id: '6.2.1',
    title: 'Full Sheet Material Sizing (48×96")',
    summary: 'Full sheet 48×96" is allowed ONLY if no holes, cuts, or rounded corners are needed.',
    fullText:
      'The 48×96" raw material is our workhorse for production efficiency. However, finishing operations (drilling, cutting, routing rounded corners) require trim margins for safe handling. If any of these finishing steps are required, the sheet must be reduced to 46.5×94" (preferred) or up to 47×94" to create safe working margins. This protects both the equipment and the final product quality.',
    applies_to: ['Rigid substrates', 'Full sheets'],
  },
  '6.2.1.1': {
    id: '6.2.1.1',
    title: 'Full Sheet Approved Materials',
    summary: 'Certain materials can safely stay at 48×96" if no finishing is needed.',
    fullText:
      'Some materials like Aluminum and ACM can remain at full 48×96" dimensions without reduction when no cutting, drilling, or finishing operations are required. This is because their structural integrity and handling characteristics allow safe processing at these dimensions. Always confirm that zero finishing work is needed before approving a full sheet.',
    applies_to: ['Aluminum', 'ACM', 'Rigid boards'],
  },
  '6.2.1.2': {
    id: '6.2.1.2',
    title: 'Materials Requiring Reduction',
    summary: 'Softer materials MUST be reduced from 48×96" when finishing is needed.',
    fullText:
      'Materials like PVC, Foamcore, Honeycomb, and Converd are softer and more prone to edge chipping or damage during finishing operations. These materials MUST be reduced to 46.5×94" (preferred) or maximum 47×94" for any job requiring holes, cuts, or corner routing. This reduction size provides adequate trim margins while minimizing material waste.',
    applies_to: ['PVC', 'Foamcore', 'Honeycomb', 'Converd'],
  },
  '6.2.2': {
    id: '6.2.2',
    title: 'Optimizing One Oversized Dimension',
    summary: 'When one dimension approaches maximum, reduce it slightly for efficiency.',
    fullText:
      'When your design has one dimension at or near 48" or 96", there is a production opportunity. If the width is 48", consider reducing to 47" to improve nesting and reduce scrap. If the length is 96", reduce to 95". These small reductions significantly improve how the material nests on our cutting systems, saving waste and production time.',
    applies_to: ['All substrates', 'Large formats'],
  },
  '6.2.3': {
    id: '6.2.3',
    title: 'Both Dimensions Near Maximum',
    summary: 'When both dimensions approach max, reduce to 46.5×46.5" for optimal nesting.',
    fullText:
      'If both the width and height are close to maximum (pushing toward 46-48" and 94-96"), consider reducing both to 46.5×46.5" or similar proportions. This creates better rectangular nesting patterns and minimizes waste, while still providing excellent sized finished pieces.',
    applies_to: ['All substrates', 'Square/near-square designs'],
  },
  '6.2.5': {
    id: '6.2.5',
    title: 'Nesting Optimization for 24" Dimension',
    summary: 'Reduce 24" to 23" when possible for better nesting efficiency.',
    fullText:
      'The 24" dimension is common for A-board inserts, sidewalk signs, and similar products. When material nesting is a factor (and the product doesn\'t have a hard requirement for 24"), reducing to 23" significantly improves how multiple pieces nest on the raw material. Exception: if the product is designed to accept standard 24" hardware (like A-board stands), the dimension must remain at 24" for proper fit.',
    applies_to: ['Rigid boards', 'A-boards', 'Sidewalk signs'],
  },
  '6.2.6': {
    id: '6.2.6',
    title: 'Value Engineering — Standard Sizes',
    summary: 'Consider efficient standard sizes like 23×17", 23×35", and 46.5×35".',
    fullText:
      'Over years of production, we\'ve identified ideal "sweet spot" sizes that nest efficiently and minimize waste: 23×17" (small), 23×35" (medium), and 46.5×35" (large). These sizes maximize material utilization and reduce production time. When a customer\'s design is close to one of these, we recommend adjustment for cost savings. Exception: A-board inserts must remain 24×36" for hardware compatibility.',
    applies_to: ['All rigid substrates', 'Value engineering'],
  },
  '6.3.1': {
    id: '6.3.1',
    title: 'Long-Term Exterior Graphics Method',
    summary: 'Long-term outdoor signs should use vinyl + laminate, not direct print.',
    fullText:
      'Direct printing on rigid substrates (like ink directly onto aluminum) is fast and economical for indoor or short-term use (under 1-2 years). However, for any sign expected to last 3+ years outdoors, the applied vinyl + laminate method is the industry standard. The laminate provides UV protection that extends the graphic life to 5-10 years and prevents color fade, chalking, and ink cracking. This is a durability difference that becomes critical for permanent installations.',
    applies_to: ['All long-term exterior signs'],
  },
  '6.5': {
    id: '6.5',
    title: 'Vinyl Type Selection (Cast vs. Calendered)',
    summary: 'Cast vinyl is required for curves, long-term exterior, and reflective. Cal is for flat, short-term.',
    fullText:
      'Vinyl comes in two types: Cast (premium) and Calendered (budget). Cast vinyl conforms to curves and textured surfaces without lifting, lasts 7-12 years, and is required for vehicle wraps, dimensional work, and compound curves. Calendered vinyl is thinner, less flexible, and best for flat surfaces with a 5-7 year lifespan. Using the wrong type for the application causes premature failure—Cal vinyl lifts off curves, and Cast is overkill for simple flat signage. Match the vinyl type to the application.',
    applies_to: ['All vinyl graphics'],
  },
  '6.5.1': {
    id: '6.5.1',
    title: 'Cut Vinyl Lettering Material Selection',
    summary: 'Cut vinyl lettering (plotted graphics) should use Cast vinyl for durability.',
    fullText:
      'Cut vinyl lettering creates beautiful sharp-edged graphics, but the cut edges are vulnerable to lifting, especially on curves or textured surfaces. Cast vinyl is more forgiving—it conforms better and the adhesive is stronger, reducing edge-lift issues. Calendered vinyl may work on very flat surfaces, but Cast is the recommended upgrade for any exterior or curved application to ensure the letters stay adhered.',
    applies_to: ['Cut vinyl lettering', 'Plotted graphics'],
  },
  '6.7.2': {
    id: '6.7.2',
    title: 'No Direct Print on Pre-Colored Black Substrates',
    summary: 'Full-color printing on pre-colored (black) substrates is prohibited for Global orders.',
    fullText:
      'Printing color inks (CMYK) onto a black substrate results in poor color vibrancy and low contrast. The black background interferes with color reproduction, and the final result looks dull and undersaturated. For any job requiring full-color graphics and Global market orders, we always specify white (or light-colored) material as the substrate base. Then CMYK printing is applied on top, delivering vivid, true colors. This is a non-negotiable requirement for Global orders due to color consistency standards.',
    applies_to: ['Global orders', 'Full-color digital print', 'Color-critical work'],
  },
  '6.8': {
    id: '6.8',
    title: 'Laminate Selection and Pairing',
    summary: 'Laminate type must match vinyl type. Cast with Cast, Cal with Cal.',
    fullText:
      'Laminate and vinyl have different thermal shrinkage rates. If you pair Cast vinyl with Calendered laminate (or vice versa), the two materials expand and contract at different rates. Over time, this causes bubbling, wrinkles, and delamination—layers separating from each other. This is a critical material compatibility rule: always match Cast vinyl with Cast laminate, and Calendered vinyl with Calendered laminate. Mixing causes field failures and customer complaints.',
    applies_to: ['All vinyl graphics', 'All laminate applications'],
  },
  '6.8.2': {
    id: '6.8.2',
    title: 'Liquid Lamination Limitations',
    summary: 'Liquid laminates do NOT provide UV protection or durability enhancement.',
    fullText:
      'Liquid spray-on laminates (like Liquid Gloss or Liquid Dry-Erase) are cost-effective for local, indoor, or short-term jobs. However, they provide minimal UV protection compared to film laminates and do NOT extend graphic life. Liquid Gloss is NOT approved for Global customer orders due to durability concerns. Use film laminate (Cast or Cal) for any outdoor or long-term application. Liquid laminates are acceptable only for local production, quick-turnaround indoor pieces.',
    applies_to: ['Local production only', 'Indoor short-term use'],
  },
  '6.12': {
    id: '6.12',
    title: 'Long-term Post-Mounted Signs — Vinyl & Lam Requirement',
    summary: 'Aluminum signs should use vinyl + laminate. Direct print is not permitted for Global.',
    fullText:
      'All long-term post-mounted signs must be produced on Aluminum. Aluminum signs always require vinyl + laminate — direct printing on aluminum is not permitted for Global orders (Exception: SSS at FSE). This ensures the 7–10 year durability expected for permanent exterior post-mounted installations.',
    applies_to: ['Aluminum substrates', 'Global orders'],
  },
  '6.14': {
    id: '6.14',
    title: 'Temporary Post-Mounted Signs — ACM Limitation',
    summary: 'ACM is NOT recommended for permanent post-mounted signs due to flex and warping.',
    fullText:
      'ACM (Aluminum Composite Material) is the standard substrate for temporary post-mounted signs and is listed as the default direct-print material for this sign type. However, ACM is not a long-term solution for post-mounted applications — it is lighter and thinner than solid aluminum and prone to flexing and warping under wind load. For long-term post-mounted applications, .080" aluminum is the industry standard. ACM is best suited for wall- or fence-mounted signs where the surface provides full structural support.',
    applies_to: ['Rigid substrates', 'Outdoor post-mounted signs'],
  },
  '6.19.2': {
    id: '6.19.2',
    title: 'Dimensional Signage Material Pairing',
    summary: 'Dimensional signage requires Cast vinyl + Cast laminate for conformability.',
    fullText:
      'Dimensional signage (3D letters, contoured shapes) requires materials that conform to curves and contoured surfaces. Only Cast vinyl + Cast laminate combination provides the necessary flexibility and adhesion strength. Calendered materials are too stiff and will not conform properly, resulting in wrinkles, bubbles, and poor aesthetics. For any dimensional or sculptural sign work, specify Cast materials exclusively.',
    applies_to: ['Dimensional letters', 'Contoured signage', '3D graphics'],
  },
  '6.19.3': {
    id: '6.19.3',
    title: 'PVC vs. Acrylic for Dimensional Elements',
    summary: 'Do NOT use PVC for dimensional elements—acrylic is more efficient and preferred.',
    fullText:
      'Both PVC and acrylic are used for dimensional letters and sculptural elements. However, acrylic is the preferred material because it machines more cleanly, requires fewer finishing steps, and results in sharper edges. PVC tends to chip, melt during cutting, and requires extensive post-processing. For dimensional work, specify acrylic to reduce production time and improve quality. PVC may be acceptable for solid blocks or simple shapes, but acrylic is the standard for detailed dimensional elements.',
    applies_to: ['Dimensional letters', 'Acrylic products', 'Custom shapes'],
  },
  '6.22.2': {
    id: '6.22.2',
    title: 'Exterior Folded Displays — Coroplast Temporary Use Only',
    summary: 'Coroplast is NOT suitable for permanent exterior signs.',
    fullText:
      'Coroplast is approved for seasonal, short-term exterior folded displays such as directional signs, event signage, and bollard covers (4mm: approx. 1–2 years; 10mm: approx. 1–3 years). Coroplast is a lightweight material that must be properly secured during installation, especially in windy areas. Coroplast is NOT suitable for permanent exterior signs. If a customer needs durability beyond 2–3 years, specify a rigid substrate (aluminum, ACM, or PVC) with vinyl + laminate.',
    applies_to: ['Temporary signage', 'Yard signs', 'Event displays'],
  },
}

// ─── SOP WARNING ENGINE ─────────────────────────────────


export function getSOPWarnings(
  category: string,
  substrate: string,
  width: number,
  height: number,
  graphicMethod: string,
  laminate: string,
  vinylType: string
): SOPWarning[] {
  const warnings: SOPWarning[] = []

    // F-060 (Candace, Small Format build note): the affordable paper sticker is the 60# decal
    // paper at 12.75 × 18.75" or smaller (either orientation) — a larger size is a different,
    // costlier build, so Sales sees it while entering the order and D&D / Estimating see it
    // on the line (sop_warnings travel with the spec).
    if (substrate === PAPER_STICKER_SUBSTRATE) {
        const [maxA, maxB] = PAPER_STICKER_AFFORDABLE_MAX;
        // CR: one dimension over the longer limit can never fit, whichever way the sticker is
        // turned — warn as soon as it is typed, before the other dimension is entered.
        const oneTooLong = width > Math.max(maxA, maxB) || height > Math.max(maxA, maxB);
        const bothEntered = width > 0 && height > 0;
        const fits = (width <= maxA && height <= maxB) || (width <= maxB && height <= maxA);
        if (oneTooLong || (bothEntered && !fits)) {
            warnings.push({ level: 'warning', text: PAPER_STICKER_OVERSIZE_TEXT });
        } else {
            warnings.push({ level: 'info', text: PAPER_STICKER_NOTE_TEXT });
        }
    }

    const longTermExterior = ['post-mounted-longterm', 'wall-fence-exterior'];
    const reduceSizeSubstrates = ['COROPLAST_4MM_WHITE', 'CONVERD_3MM_WHITE', 'HONEYCOMB_BOARD_0.5IN_W-K-W', 'FOAMCORE_0.1875IN_WHITE_ECONOM'];
    // Full-sheet approved materials (SOP 6.2.1.1) — shared with isFullSheetFinishingLocked
    const fullSheetApproved = FULL_SHEET_APPROVED_SUBSTRATES as unknown as string[];

    // 6.3.1 — Long-term exterior + direct print
    if (longTermExterior.includes(category) && (graphicMethod === 'direct-1st' || graphicMethod === 'direct-2nd')) {
        warnings.push({ level: 'error', text: 'SOP 6.3.1: Long-term exterior signage should use vinyl + laminate instead of direct print for durability and fade resistance.' });
    }

    // 6.12 — Aluminum + direct print for global
    if ((substrate === 'ALUMINUM_.080IN_WHITE' || substrate === 'ALUMINUM_.080IN_MILL' || substrate === 'ALUMINUM_.040IN_WHITE') && (graphicMethod === 'direct-1st' || graphicMethod === 'direct-2nd')) {
        warnings.push({ level: 'warning', text: 'SOP 6.12: Aluminum signs should use vinyl & lam — direct printing on aluminum is not permitted for Global customers.' });
    }

    // 6.2.1 — Full sheet 48×96 warnings (enhanced from SOP pp.7-9)
    const is48x96 = (width >= 48 && height >= 96) || (width >= 96 && height >= 48);
    if (is48x96) {
        if (reduceSizeSubstrates.includes(substrate)) {
            // 6.2.1.2 — These materials MUST reduce at 48×96
            warnings.push({ level: 'error', text: 'SOP 6.2.1.2: ' + substrate.replace(/_/g, ' ') + ' at 48\u00D796" MUST be reduced. Preferred: 46.5\u00D794". Max: 47\u00D794" or 46\u00D795".' });
        } else if (fullSheetApproved.includes(substrate)) {
            // 6.2.1.1 — Approved materials CAN stay at 48×96 only if no bleed/cut
            warnings.push({ level: 'warning', text: 'SOP 6.2.1: Full sheet 48\u00D796" is allowed ONLY if no holes, cuts, or rounded corners are needed. If ANY finishing is required, reduce to 46.5\u00D794" (preferred) or 47\u00D794" (max).' });
        }
    }

    // 6.2.6 — Value Engineering Sizes. F-041 (Amanda): when the entered size is one of the
    // SOP's Value Engineering sizes, that is THE recommendation — the generic one-side hints
    // (6.2.5 "24 → 23", 6.2.2 "48 → 47") would sit next to it and read as a different
    // suggestion (24×36 showed "reduce 24" to 23"" = 23×36 beside "consider 23×35").
    const isSize = (a: number, b: number) => (width == a && height == b) || (height == a && width == b);
    const valueEngineering: string | null = isSize(24, 18)
        ? 'SOP 6.2.6: Value Engineering — consider 23\u00D717" for better nesting.'
        : isSize(24, 36)
            ? 'SOP 6.2.6: Value Engineering — consider 23\u00D735". Exception: A-board inserts must remain 24\u00D736" for hardware.'
            : isSize(48, 36)
                ? 'SOP 6.2.6: Value Engineering — consider 46.5\u00D735" for better nesting.'
                : null;

    // 6.2.2 — One side 48 or 96 (but not both)
    const oneSide48 = (width == 48 && height < 96) || (height == 48 && width < 96);
    const oneSide96 = (width == 96 && height < 48) || (height == 96 && width < 48);
    if (oneSide48 && !is48x96 && valueEngineering === null) {
        warnings.push({ level: 'info', text: 'SOP 6.2.2: One side is 48" — consider reducing to 47". The other side should be no more than 94".' });
    }
    if (oneSide96 && !is48x96) {
        warnings.push({ level: 'info', text: 'SOP 6.2.2: One side is 96" — consider reducing to 95". The other side should be no more than 46".' });
    }

    // 6.2.5 — 24" wide signs (rigid board substrates with 48x96 raw size only)
    const subObj6 = SUBSTRATES.find(s => s.id === substrate);
    const isRigid48x96 = subObj6 && subObj6.matType === 'rigid' && subObj6.sizes && subObj6.sizes.some(sz => sz.w === 48 && sz.h === 96);
    if ((width == 24 || height == 24) && isRigid48x96 && valueEngineering === null) {
        warnings.push({ level: 'info', text: 'SOP 6.2.5: When possible, reduce 24" dimension to 23" for better material nesting and efficiency. Exception: A-board inserts must stay at 24" for hardware fit.' });
    }

    if (valueEngineering !== null) {
        warnings.push({ level: 'info', text: valueEngineering });
    }

    // 6.2.3 — Square limit
    if (width >= 46 && height >= 46 && width <= 48 && height <= 48) {
        warnings.push({ level: 'info', text: 'SOP 6.2.3: If both sides are near max, reduce to 46.5\u00D746.5".' });
    }

    // ── CAST / CAL MIXING PREVENTION (based on BASE SUBSTRATE, not applied vinyl) ──
    // This restriction applies when the substrate IS a Cast or Cal vinyl (standalone vinyl sign)
    // It does NOT apply when vinyl is applied to a rigid board
    const subIsCast = isSubstrateCast(substrate);
    const subIsCal = isSubstrateCal(substrate);
    const isCastLam = CAST_LAM_IDS.includes(laminate);
    const isCalLam = CAL_LAM_IDS.includes(laminate);

    if (subIsCast && isCalLam) {
        warnings.push({ level: 'error', text: 'SOP: Do NOT mix Cast vinyl substrate with Calendered laminate. Cast and Cal have different shrinkage rates — mixing causes delamination, bubbling, and premature failure. Use Cast laminate (Cast Gloss or Cast Matte) with Cast vinyl substrates.' });
    }
    if (subIsCal && isCastLam) {
        warnings.push({ level: 'error', text: 'SOP: Do NOT mix Calendered vinyl substrate with Cast laminate. Different expansion/contraction rates cause the layers to separate over time. Use Cal laminate (Cal Gloss or Cal Matte) with Cal vinyl substrates.' });
    }

    // 6.5 — Cast vinyl recommended for cut vinyl lettering (applied vinyl context)
    const isCalAppliedVinyl = ['VINYL_CAL_INTERMEDIATE', 'VINYL_CAL_FLOOR', 'VINYL_OPTICALLY_CLEAR_CAL', 'VINYL_WALL_TEXTURED_SURFACE'].includes(vinylType);
    const isCastAppliedVinyl = ['VINYL_CAST', 'VINYL_CAST_VEHICLE', 'CAST_RTA'].includes(vinylType);
    if (graphicMethod === 'cut-vinyl' && isCalAppliedVinyl) {
        warnings.push({ level: 'warning', text: 'SOP 6.5.1: Cast vinyl is recommended for all Cut Vinyl Lettering (plotted graphics). Cal vinyl may lift on curves and textured surfaces.' });
    }

    // 6.5.2 — Cast vinyl required for exterior dimensional
    if (category === 'dimensional' && graphicMethod === 'vinyl-applied' && !isCastAppliedVinyl && vinylType) {
        warnings.push({ level: 'warning', text: 'SOP 6.5/6.19.2: Dimensional signage requires Cast vinyl (VINYL_CAST + LAM_CAST) for conformability on contoured surfaces.' });
    }

    // 6.7.2 — Pre-colored substrates (black) — cut only, no full-color printing for Global
    const blackSubstrates = ['COROPLAST_4MM_BLACK', 'POLYSTYRENE_.060IN_BLACK', 'PVC_12MM_BLACK_INDOOR', 'PVC_3MM_BLACK_INDOOR', 'PVC_6MM_BLACK_INDOOR'];
    if (blackSubstrates.includes(substrate) && (graphicMethod === 'direct-1st' || graphicMethod === 'direct-2nd')) {
        warnings.push({ level: 'error', text: 'SOP 6.7.2: Full-color printing on pre-colored (black) substrates is prohibited for Global customer orders. Use white material with standard CMYK printing.' });
    }

    // 6.8 — Liquid lam warnings
    if (laminate === 'LIQUID_DRY-ERASE_LAM' || laminate === 'LIQUID_GLOSS_LAM') {
        warnings.push({ level: 'warning', text: 'SOP 6.8: Liquid lamination does NOT enhance durability and does NOT provide UV protection.' });
    }
    if (laminate === 'LIQUID_GLOSS_LAM') {
        warnings.push({ level: 'error', text: 'SOP 6.8.2: Liquid Gloss Lam is NOT approved for Global customer builds. Local use only.' });
    }

    // 6.14.3 — ACM not for long-term post-mounted
    if (category === 'post-mounted-longterm' && (substrate === 'ACM_3MM_WHITE' || substrate === 'ACM_6MM_WHITE')) {
        warnings.push({ level: 'warning', text: 'SOP 6.14: ACM is NOT recommended for long-term post-mounted signs — it is prone to flexing and warping under wind load. Use .080" aluminum for post-mounted applications.' });
    }

    // 6.19.3 — PVC for dimensional
    if (category === 'dimensional' && substrate.startsWith('PVC_')) {
        warnings.push({ level: 'error', text: 'SOP 6.19.3: Do NOT use PVC for dimensional elements — acrylic is more efficient for production workflows.' });
    }

    // 6.22.2.3 — Coroplast not for permanent exterior
    if (longTermExterior.includes(category) && (substrate === 'COROPLAST_4MM_WHITE' || substrate === 'COROPLAST_10MM_WHITE')) {
        warnings.push({ level: 'error', text: 'SOP 6.22.2: Coroplast is NOT suitable for permanent exterior signs.' });
    }

    // ── NEW SOP RULES (v4.20.0) ──

    // 6.16.2 — Magnetic substrates: no holes, no pressure laminate
    if (substrate === 'SCM_3MM_WHITE') {
        warnings.push({ level: 'warning', text: 'SOP 6.16.2: Magnetic board (SCM) — NO holes permitted. NO pressure laminate. Use liquid laminate only if needed.' });
    }

    // 7.1.1 / 7.1.2 — Full-sheet print material block
    const noFullSheetPrint = ['CONVERD_3MM_WHITE', 'COROPLAST_4MM_WHITE', 'HONEYCOMB_BOARD_0.5IN_W-K-W', 'FOAMCORE_0.1875IN_WHITE_ECONOM', 'ALUMINUM_.080IN_WHITE', 'ALUMINUM_.080IN_MILL'];
    if (noFullSheetPrint.includes(substrate) && is48x96 && (graphicMethod === 'direct-1st' || graphicMethod === 'direct-2nd')) {
        warnings.push({ level: 'error', text: 'SOP 7.1: ' + substrate.replace(/_/g, ' ') + ' is NOT approved for full-sheet (48\u00D796) direct print. Reduce dimensions or select a different material.' });
    }

    // 6.23.4.5 — Frosted vinyl: no laminate, max 51.5" print
    if (vinylType === 'VINYL_RTA_FROSTED' || substrate === 'VINYL_RTA_FROSTED') {
        if (laminate && laminate !== 'none') {
            warnings.push({ level: 'error', text: 'SOP 6.23.4.5: Frosted RTA vinyl must have NO laminate — laminate eliminates the frosted effect.' });
        }
        if (width > 51.5 || height > 51.5) {
            warnings.push({ level: 'warning', text: 'SOP 6.23.4.5: Frosted vinyl max recommended print dimension is 51.5" — larger sizes may curl.' });
        }
    }

    // 6.23.2.1 — Floor vinyl must use floor-rated laminate
    if ((vinylType === 'VINYL_CAL_FLOOR' || substrate === 'VINYL_CAL_FLOOR') && laminate && laminate !== 'none' && laminate !== 'LAM_FLOOR') {
        warnings.push({ level: 'error', text: 'SOP 6.23.2.1: Floor vinyl MUST use floor-rated slip-resistant laminate (LAM_FLOOR) for safety compliance.' });
    }

    // 6.23.4.2 — Perforated vinyl: no complex cuts
    const perforatedVinyls = ['VINYL_PERFORATED_STOREFRONT', 'VINYL_PERFORATED_VEHICLE'];
    if (perforatedVinyls.includes(vinylType) || perforatedVinyls.includes(substrate)) {
        warnings.push({ level: 'warning', text: 'SOP 6.23.4.2: Perforated vinyl — avoid complex/contour cuts. Use simple rectangular shapes only.' });
    }

    // 6.9.1 / 6.9.2 — Clear acrylic: recommend 2nd surface print
    const clearSubstrates = ['ACRYLIC_0.125IN_CLEAR', 'ACRYLIC_0.25IN_CLEAR', 'ACRYLIC_0.5IN_CLEAR'];
    if (clearSubstrates.includes(substrate) && graphicMethod === 'direct-1st') {
        warnings.push({ level: 'warning', text: 'SOP 6.9.1: Clear acrylic — consider 2nd Surface Print (back of panel) so graphics are viewed through the clear face. 1st surface print reveals mounting hardware underneath. Add Spot White for opacity.' });
    }

    // 6.6 — Small format: EVT only (informational, not blocking)
    const smallFormatCats = ['folded-displays'];
    if (smallFormatCats.includes(category)) {
        warnings.push({ level: 'info', text: 'SOP 6.6: Small format products (table tents, counter displays) are only available at the Everett (EVT) location.' });
    }

    return warnings;
}
